/**
 * Global client state — spec §F1 ("Global state (Zustand store lib/state.ts) — port the
 * mockup's `state` object"). The `AppState` interface below is the spec's, verbatim;
 * `AppActions` carries the M1 setters. Server state lives in TanStack Query (lib/api.ts),
 * never here.
 */
import { create } from 'zustand';
import { isCompactViewport } from './viewport';

import { ringId } from './aoi';
import type { ToolId } from './analysis';

/** §I1 roles, lowest privilege first. */
export type Role = 'public' | 'field' | 'partner' | 'analyst' | 'manager';

export type Country = 'ALL' | 'BFA' | 'MLI' | 'NER' | 'NGA';

/**
 * §F5.1 basemap keys.
 *
 * `dark` is the CARTO dark variant, which only a deployment holding a CARTO URL or key
 * serves (`/meta/config.basemaps`, `apps/meta/basemaps.py`). It is in the union because the
 * store and the permalink's `bm=` have to be able to *hold* it on such a deployment; the
 * catalogue, not the type, decides whether it is offered — see `features/map/basemaps.ts`.
 */
export type Basemap = 'light' | 'dark' | 'streets' | 'satellite' | 'terrain';

/** The overlay-hierarchy reporting modes (§M3.5) — levels that are not admin levels. */
export const WATERSHED_LEVEL = 'watershed';
export const PARTNERSHIP_LEVEL = 'partnership';

/**
 * The two overlay hierarchies, as reporting modes.
 *
 * They are not interchangeable, and the difference is the whole reason `watershedReady`
 * exists: MELS reports B.2.1/C.2.1 **natively** per watershed, so that radio is live only
 * for an indicator that has such values; nothing reports natively per partnership, so §E4's
 * area-weighted roll-up is the only answer there ever is and the radio is live for every
 * indicator that has commune values at all.
 */
export const OVERLAY_LEVELS = [WATERSHED_LEVEL, PARTNERSHIP_LEVEL] as const;

export function isOverlayLevel(level: unknown): level is OverlayLevel {
  return level === WATERSHED_LEVEL || level === PARTNERSHIP_LEVEL;
}

/**
 * §F1 `reportingLevel`, widened by spec §M3.5.
 *
 * §F1 wrote this as `0 | 1 | 2` because the watershed radio was disabled: watershed values
 * were to be area-weighted from admin cells later. Part M changed that — MELS serves B.2.1
 * and C.2.1 **per `watershed_id`**, so a watershed has its own reported number and the
 * radio becomes meaningful for those indicators ("the Operational-hierarchy watershed radio
 * can be enabled for MELS-backed indicators … as soon as M3 lands"). `'watershed'` is
 * therefore a fourth reporting mode rather than a fourth admin level: the choropleth draws
 * `type=watershed` polygons and asks `/values?level=watershed` (docs/NOTES.md).
 */
export type OverlayLevel = (typeof OVERLAY_LEVELS)[number];
export type ReportingLevel = 0 | 1 | 2 | OverlayLevel;

/** The admin level to fall back on where only `0|1|2` makes sense (bulk rows, labels). */
export function adminLevelOf(level: ReportingLevel, fallback: 0 | 1 | 2 = 1): 0 | 1 | 2 {
  return isOverlayLevel(level) ? fallback : level;
}
/**
 * The i18n key naming a reporting level — one table, because the legend footer (§F5.6),
 * the choropleth tooltip (§F5.3) and the indicator pane's "mapped at …" line all print it
 * and disagreeing would mean the map and its legend describe different geographies.
 */
const LEVEL_LABEL_KEYS: Record<string, string> = {
  0: 'indicators.levelCountry',
  1: 'indicators.levelRegion',
  2: 'indicators.levelCommune',
  [WATERSHED_LEVEL]: 'indicators.levelWatershed',
  [PARTNERSHIP_LEVEL]: 'indicators.levelPartnership',
};

export function levelLabelKey(level: ReportingLevel | number | null | undefined): string {
  return LEVEL_LABEL_KEYS[String(level)] ?? 'indicators.levelRegion';
}

/* ---------------------------------------------- §F5.8 scope (the cascading filter) */

/**
 * The unit a reader has filtered the whole Map Explorer down to.
 *
 * §F1's country selector narrows the map to one of four countries; the client asked for the
 * same thing one, two and three levels further down — "pick a region and everything follows,
 * the numbers included". A scope is that pick: one `org_units` row, and the map draws its
 * subtree and nothing else (`?parent=` on §D2/§D3 — see `apps/hierarchy/subtree.py`).
 *
 * `country` and `name` are carried rather than looked up because both are needed *without* a
 * round trip: `country` so the global selector and the scope can never contradict each other
 * (see `setScope`/`setCountry`), `name` so the §F5.10 status crumb and the widget hint can
 * say where the reader is the moment they pick it. The permalink stores only `unitId` and
 * `app/PermalinkSync` rebuilds the rest from `/hierarchy/units/{id}` — a scope is a fact
 * about the server's hierarchy, and a hash is not allowed to assert one.
 *
 * A **country** is deliberately not a scope: §F1 already has a global control for that, two
 * filters meaning the same thing would be two ways to disagree, and picking a country in the
 * cascade therefore writes `country`. The type still admits `adminLevel: 0` because a
 * hand-edited `sc=BF` must resolve to something rather than crash the boot.
 */
export interface Scope {
  unitId: string;
  unitType: 'admin' | 'watershed' | 'partnership';
  /** `0|1|2` for an admin unit, `null` for a watershed or a partnership. */
  adminLevel: 0 | 1 | 2 | null;
  /**
   * The unit above this one (§D2 `parent_id`) — how the cascade's *upper* select knows what
   * to show. A commune scope has to leave "Djoro" standing in the Region box, and the only
   * other way to find that out would be to search the region list for a parent match.
   */
  parentId: string | null;
  /** ISO3 of the unit, or `null` for a cross-border watershed (§C). */
  country: string | null;
  /** The unit's own name — the §F5.10 crumb, without a fetch. */
  name: string;
}

/**
 * A §D2 unit row — from `/hierarchy/units/{id}` or `/hierarchy/options` — as a `Scope`.
 *
 * Structural rather than typed against `lib/api.ts` because that module imports *this* one;
 * the shared shape is the point, so a permalink restore and a dropdown pick can never build
 * two different scopes for the same unit.
 */
export function scopeFromUnit(unit: {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
}): Scope {
  return {
    unitId: unit.id,
    unitType: unit.unit_type as Scope['unitType'],
    adminLevel: unit.admin_level as Scope['adminLevel'],
    parentId: unit.parent_id,
    country: unit.country,
    name: unit.name,
  };
}

/** Every reporting level, in §F5.8 row order — what "no filter" allows. */
export const ALL_REPORTING_LEVELS: readonly ReportingLevel[] = [
  0, 1, 2, WATERSHED_LEVEL, PARTNERSHIP_LEVEL,
];

/**
 * The level an **unfiltered** map reports at — client review R5.
 *
 * R5 made the reporting level a function of the cascade rather than a control of its own
 * ("reporting level derives from the cascade: none→regions; country→its regions; region→its
 * communes; watershed→watershed/partnerships"). The five §F5.8 radios are gone, so this is
 * the level every path back out of a filter lands on: `clearScope`, and the country change
 * that drops a scope belonging to somewhere else.
 *
 * It is deliberately *not* `lastAdminLevel` any more. That field existed to remember which
 * radio the reader had pressed before a filter borrowed the level; with no radios there is
 * nothing to remember, and "the level you were at before you filtered" and "the level the
 * unfiltered map opens at" are now the same fact. `lastAdminLevel` survives as the §M3.5
 * fallback target for `'watershed'` (a permalink can still name that mode).
 */
export const NO_SCOPE_LEVEL: ReportingLevel = 1;

/**
 * The reporting levels a scope leaves meaningful — the §F5.8 radios that stay live.
 *
 * The rule is "the scope unit, or its children": a region is mapped by painting its
 * communes, a commune by painting itself, a watershed by painting itself **or** its
 * partnerships (the one scope with a genuine choice), a partnership by painting itself.
 * Anything else would ask §D3 for units that are not in the subtree, i.e. for an empty map.
 *
 * A country scope (only reachable from a hand-edited hash — see `Scope`) allows both admin
 * levels below it, and defaults to regions, which is where the unscoped map already opens.
 */
export function scopeLevels(scope: Scope | null): readonly ReportingLevel[] {
  if (!scope) return ALL_REPORTING_LEVELS;
  if (scope.unitType === WATERSHED_LEVEL) return [WATERSHED_LEVEL, PARTNERSHIP_LEVEL];
  if (scope.unitType === PARTNERSHIP_LEVEL) return [PARTNERSHIP_LEVEL];
  return scope.adminLevel === 0 ? [1, 2] : [2];
}

/** The level a scope opens on: the first one it allows. */
export function defaultScopeLevel(scope: Scope): ReportingLevel {
  return scopeLevels(scope)[0] as ReportingLevel;
}

/**
 * The reporting level the scope unit **itself** is served at.
 *
 * `?parent=X` is inclusive, so asking §D3 for X's own level under X's own scope returns
 * exactly one row: X's value. That is how §F5.9's dock popover gets "the number for the
 * thing you filtered to" without a per-unit endpoint — and it is *not* `defaultScopeLevel`,
 * which for a region is the level of its children.
 */
export function scopeOwnLevel(scope: Scope): ReportingLevel {
  if (scope.unitType === WATERSHED_LEVEL) return WATERSHED_LEVEL;
  if (scope.unitType === PARTNERSHIP_LEVEL) return PARTNERSHIP_LEVEL;
  return scope.adminLevel ?? 1;
}

/**
 * `wanted` if the scope allows it, else `fallback`, else the scope's default.
 *
 * Keeping `wanted` is what makes the level *sticky* across a scope change — a reader
 * comparing two watersheds at partnership level stays at partnership level when they pick
 * the second one — and it is also how a permalink's `lv=` survives its own `sc=`.
 */
export function levelWithinScope(
  scope: Scope | null,
  wanted: ReportingLevel,
  fallback: ReportingLevel,
): ReportingLevel {
  const allowed = scopeLevels(scope);
  if (allowed.includes(wanted)) return wanted;
  if (allowed.includes(fallback)) return fallback;
  return allowed[0] as ReportingLevel;
}

/* ------------------------------------------- client P1: an indicator-aware level */

/**
 * The catalogue facts that decide **where an indicator can be painted** — §D3's `levels`,
 * `aggregation` and `has_watershed_values`, and nothing else.
 *
 * A structural type rather than `Indicator`, so `lib/state.ts` keeps knowing nothing about
 * `lib/api.ts` (it is imported by the store, the map and every test) and so a test can pass
 * the three fields it is exercising.
 */
export interface LevelCapableIndicator {
  aggregation: 'sum' | 'mean' | 'none';
  /** §D3 `levels` — the strata this indicator has **stored** values at. */
  levels?: readonly ReportingLevel[];
  has_watershed_values?: boolean;
}

/**
 * The levels an indicator can actually be painted at — the set the snap and the chooser
 * both read.
 *
 * **The two kinds of series answer this differently, and that is the whole design.**
 *
 * * `aggregation: 'none'` — a survey estimate. §E4 serves it at the geographies it was
 *   estimated at and **nowhere else**: there is no roll-up, so a level with no stored rows
 *   is genuinely, permanently empty. `levels` is therefore the literal truth, and it is what
 *   this returns.
 * * `sum` / `mean` — §E4 rolls children up and area-weights overlays, so it can value any
 *   admin level from any other. `levels` *understates* those: on a production stack with no
 *   demo seed, `b21` stores at country and watershed only, and reading its `levels` as "the
 *   levels that work" would drag every unfiltered view off regions — a behaviour change for
 *   25 indicators, to fix a problem none of them has. They get the full admin ladder plus
 *   partnerships (always §E4-derivable), and `'watershed'` only on §M3.5's existing native
 *   test, which is the rule those indicators already lived under.
 *
 * An indicator the catalogue has not answered for yet is `null`, and every caller reads that
 * as "do not act" — the same rule `snapYearToData` applies to an empty year list. Snapping
 * on an unanswered catalogue would move the level on every boot.
 */
export function effectiveLevels(
  meta: LevelCapableIndicator | null | undefined,
): readonly ReportingLevel[] {
  if (!meta) return [];
  if (meta.aggregation === 'none') return meta.levels ?? [];
  return [
    0,
    1,
    2,
    ...(meta.has_watershed_values ? ([WATERSHED_LEVEL] as const) : []),
    PARTNERSHIP_LEVEL,
  ];
}

/**
 * The levels reachable **without leaving the current filter** — what the chooser offers and
 * what the snap may choose from.
 *
 * `scopeLevels` is "the scope unit's children", which is the right rule for a cascade that
 * drills down. It is one level short for this question: §D3's `?parent=` is *inclusive*, so
 * asking for the scope's own level under its own scope returns the scope itself (this is how
 * §F5.9's dock gets "the number for the thing you filtered to" — `scopeOwnLevel`). That is
 * exactly the case the client's report turns on: **a country scope can paint level 0**, one
 * polygon carrying that country's own total, which is where a baseline indicator's data is.
 * Without the union a country filter would have no reachable level at all for the baseline
 * and the map would stay blank inside a filter the reader can see is set.
 *
 * **A country scope also reaches the two overlay hierarchies**, and a region or commune scope
 * does not. That is not a preference, it is the shape of the data: a watershed's parent is a
 * *country*, so `?parent=BF` narrows the watershed level to Burkina Faso's watersheds and
 * `?parent=BF57` narrows it to none at all. Client observation P1(d) asks for exactly the
 * first of those — "scoping by country shows that country's valued watersheds" — and the
 * second is why the region case is the honest empty state rather than a silent widening.
 */
export function reachableLevels(scope: Scope | null): readonly ReportingLevel[] {
  const allowed = scopeLevels(scope);
  if (!scope) return allowed;
  const own = scopeOwnLevel(scope);
  const overlays =
    scope.unitType === 'admin' && scope.adminLevel === 0 ? OVERLAY_LEVELS : [];
  return ALL_REPORTING_LEVELS.filter(
    (level) => level === own || allowed.includes(level) || overlays.includes(level as never),
  );
}

/**
 * The level the §F5.8 cascade derives on its own — R5's table, as one expression.
 *
 * No filter is regions (`NO_SCOPE_LEVEL`); a country is still regions, because a country is
 * not a `Scope` (see `Scope`); anything else is the scope's own first allowed level. This is
 * the level `clearScope` returns to and the level `setScope` falls back on, so it is written
 * once here rather than restated by every caller that needs to ask "what would the cascade
 * say, ignoring the indicator?".
 *
 * It exists for the **un-snap** (below). A snap that could not be undone was a one-way trip:
 * mapping a 2025 baseline series took an unfiltered map to countries, and going back to any
 * of the 27 roll-up indicators left it there — four polygons instead of the eighty-two R5
 * promises, with no control on screen that could ask for regions again (the chooser is
 * refused for roll-ups by design, and "Clear filter" is inert with no filter set).
 */
export function cascadeLevel(scope: Scope | null): ReportingLevel {
  return scope ? defaultScopeLevel(scope) : NO_SCOPE_LEVEL;
}

/**
 * The level to actually report at: `wanted`, or the nearest level that has data inside the
 * current geography — client observation P1(b).
 *
 * Since client review R5 the level has been a pure function of the cascade: no filter means
 * regions, a country means its regions. For the 2025 MELS baseline that is a level with no
 * rows and no way to derive any, so **the entire load painted nothing** on the view every
 * reader opens on. This is the repair, and it is deliberately narrow:
 *
 * * `available` empty — the catalogue has not answered, or the indicator holds nothing.
 *   No-op. "We do not know yet" must never be read as "there is nothing here".
 * * `wanted` is available — no-op. This is every roll-up indicator, always
 *   (`effectiveLevels` gives them the whole ladder), so their behaviour is exactly today's.
 * * nothing available is reachable in this geography — no-op. A region or commune filter
 *   over a survey estimate keeps the honest empty state: the data really is not there, and
 *   silently jumping the map back out to countries would answer a question the reader did
 *   not ask.
 * * otherwise — the **nearest** reachable available level, measured along §F5.8 row order
 *   (`ALL_REPORTING_LEVELS`). Unfiltered or country-filtered, `wanted` is regions and the
 *   baseline offers countries and watersheds; countries is one row away and watersheds
 *   three, so the map paints the four country polygons with their own totals. Ties go to the
 *   coarser level, which is the one that always has somewhere to draw.
 */
export function snapLevelToData(
  scope: Scope | null,
  wanted: ReportingLevel,
  available: readonly ReportingLevel[],
): ReportingLevel {
  if (!available.length || available.includes(wanted)) return wanted;
  const reachable = reachableLevels(scope);
  const candidates = available.filter((level) => reachable.includes(level));
  if (!candidates.length) return wanted;
  const rank = (level: ReportingLevel) => ALL_REPORTING_LEVELS.indexOf(level);
  const target = rank(wanted);
  return candidates.reduce((best, level) =>
    Math.abs(rank(level) - target) < Math.abs(rank(best) - target) ? level : best,
  );
}

/**
 * The levels a chooser should offer for this indicator here — empty when there is no
 * choice to make (client observation P1(c)).
 *
 * Two rules, and both are about *not* putting a control on screen:
 *
 * * **roll-up indicators are excluded outright.** Their level derives from the cascade and
 *   has since R5; `effectiveLevels` gives them every level, so a chooser would offer five
 *   buttons that contradict the filter above them. Only `aggregation: 'none'` — a series
 *   that genuinely exists at some strata and not others — has a choice worth showing.
 * * **fewer than two is not a choice.** One reachable level is the level; zero is the empty
 *   state, which the legend already explains.
 *
 * Returned in §F5.8 row order so "Countries | Watersheds" reads the way the Boundaries panel
 * lists them, whatever order the API's `levels` arrived in.
 */
export function chooserLevels(
  meta: LevelCapableIndicator | null | undefined,
  scope: Scope | null,
): readonly ReportingLevel[] {
  if (!meta || meta.aggregation !== 'none') return [];
  const available = effectiveLevels(meta);
  const reachable = reachableLevels(scope);
  const offered = ALL_REPORTING_LEVELS.filter(
    (level) => available.includes(level) && reachable.includes(level),
  );
  return offered.length > 1 ? offered : [];
}

/** §D2/§D3 `?parent=` for the current scope — `null` is "the whole programme". */
export const selectScopeParent = (s: Pick<AppState, 'scope'>): string | null =>
  s.scope?.unitId ?? null;

/* ---------------------------------------------------- Task 6: dated NDVI series */

/**
 * The two dated NDVI series (§D5 `ndvi_terra`/`ndvi_aqua`, Task 6) — the only rasters
 * `ndviMask`'s stencil overlay is allowed to sit above (`RasterLayers.tsx`).
 *
 * Defined here rather than in `features/map/epochs.ts`, which otherwise owns every other
 * temporal helper: `epochs.ts` imports `Layer`/`LayerEpoch` from `lib/api.ts`, and `lib/
 * api.ts` already imports `Role`/`ReportingLevel`/etc from *this* module — so a store import
 * of `epochs.ts` would close that into a cycle. `epochs.ts` re-exports it, so every other
 * caller still reads `NDVI_SERIES` from the module that holds the rest of the temporal
 * helpers; only the store itself has to reach one module up for it.
 */
export const NDVI_SERIES = ['ndvi_terra', 'ndvi_aqua'] as const;

/* ------------------------------------------- client review R4: one raster at a time */

/**
 * Which §D5 catalogue rows reach the map as **tiles** — the register R4's invariant needs.
 *
 * R4: *"no raster-over-raster — while one raster layer is on, other raster rows are disabled
 * … vector layers combine freely"*. The rule has to live in the store, because two separate
 * controls now switch layers on (the §F5.2 Layers pane and R8's layer manager) and a rule
 * implemented in either of them would be a rule the other could break.
 *
 * But `toggleLayer(id)` is given an **id**, and whether that id is a raster is a fact about
 * the §D5 catalogue — server state, which lives in TanStack Query and must not be copied
 * into `AppState` (the §F1 interface is fixed verbatim, `shareHash` round-trips it and
 * `state.test.ts` asserts it field by field). So the catalogue's raster ids are held here, in
 * a module-level register that whoever holds the `['layers', role]` query writes on arrival
 * (`useRasterRegistry`, features/map/layerKinds.ts).
 *
 * An **empty** register means no layer is a raster, so the invariant simply does not bite
 * until the catalogue has answered. That is the right failure: a rule that cannot yet be
 * evaluated must not block a reader, and the pane's own tooltip is derived from the same
 * register, so control and rule can never disagree about which rows are rasters.
 *
 * **Registering is also enforcing** (R6 fix). `toggleLayer` guards the two panes, but it is
 * not the only writer of `layersOn`: `permalink.applyToStore` assigns the whole set through
 * `setState`, and it *must* — it runs before `createRoot`, so `/layers` does not exist yet and
 * the register is still empty, which means `toggleLayer` could not evaluate the rule there
 * either. `#ly=soc,sgphwms` therefore hydrated two rasters, `RasterLayers` stacked them
 * opaquely, and the pane then disabled the control for the one on *top* — the reader could
 * not switch off the layer they were looking at from either pane. So the moment the catalogue
 * makes the rule evaluable, the set is normalised to satisfy it: the invariant holds for every
 * writer, including ones written later, because it is re-established on the arrival of the
 * only fact that can decide it.
 */
let RASTER_IDS: ReadonlySet<string> = new Set<string>();

export function registerRasterLayers(ids: Iterable<string>): void {
  RASTER_IDS = new Set(ids);
  useAppStore.setState((s) => {
    // The **first** raster in `layersOn` survives, which for a permalink is the first one the
    // hash named — the same row `activeRasterOf` reports as active, so the legend, the two
    // panes and the map all name the layer that stayed.
    const kept = activeRasterOf(s.layersOn);
    if (kept === null) return {};
    const next = new Set<string>();
    for (const id of s.layersOn) if (id === kept || !RASTER_IDS.has(id)) next.add(id);
    return next.size === s.layersOn.size ? {} : { layersOn: next };
  });
}

export function isRasterLayerId(id: string): boolean {
  return RASTER_IDS.has(id);
}

/**
 * The one raster currently drawn, or `null` — R4's whole state, derived rather than stored.
 *
 * Derived because a stored copy is a second source of truth for "is this layer on", and the
 * legend's ✕, the pane checkbox, the manager checkbox and a permalink restore all write
 * `layersOn` without going near it. Returns an id (a stable string), so a component
 * subscribing through it re-renders only when the answer actually changes.
 */
export function activeRasterOf(layersOn: ReadonlySet<string>): string | null {
  for (const id of layersOn) if (RASTER_IDS.has(id)) return id;
  return null;
}

export const selectActiveRaster = (s: Pick<AppState, 'layersOn'>): string | null =>
  activeRasterOf(s.layersOn);

/* ------------------------------------- client round 4 E2: one mapping at a time */

/** The §F5.2 pane tabs, named — the two mappings a reader can put on the map. */
export type SidebarTab = 'inds' | 'layers';

/** Anything on the indicator side? §F5.2's fill, §F5.5's overlays, §F5.4's mode. */
export const selectIndicatorMapped = (
  s: Pick<AppState, 'indicator' | 'overlays' | 'biv'>,
): boolean => Boolean(s.indicator) || s.overlays.length > 0 || s.biv.on;

/** Anything on the layer side? The D2 raster pick and every vector added under it. */
export const selectLayersMapped = (s: Pick<AppState, 'layersOn'>): boolean =>
  s.layersOn.size > 0;

/* ------------------------- client round 4 E4: the scope mask, and what it outranks */

/**
 * Is the cascade filter's geography set at all? — a scope, **or** the country rung alone.
 *
 * The country box is the first of the cascade's five rungs (`CascadeFilter`), and picking in
 * it writes `country` and no `scope`: there is no `Scope` for a country, because §D3 asks for
 * a *parent* and the country's children are what the map is already drawing. So a state with
 * `country: 'BFA'` and no scope is a filtered map by every other reading — the choropleth is
 * one country's regions, `clearScope` offers to clear it (D3) — and E4's mask has to agree,
 * which is the round 5 correction: it did not, and a country-level filter left `soc` painted
 * across all four countries.
 */
export function filterGeographySet(s: Pick<AppState, 'scope' | 'country'>): boolean {
  return Boolean(s.scope) || s.country !== 'ALL';
}

/**
 * Is the raster masked to the §F5.8 filter's geography right now?
 *
 * Three facts, and the order they are read in **is** E4's precedence rule (docs/NOTES.md):
 *
 * 1. the filter's geography is set — the mask has an outline to cut to. A scope, or the
 *    cascade's country rung on its own (`filterGeographySet`);
 * 2. the manual AOI clip is **off** — an AOI clip is a reader asking for this exact thing
 *    with a polygon of their own, and one element has one `clip-path`, so the hand-drawn one
 *    wins. Were it the other way round, pressing *Clip* under a filter would do nothing
 *    visible and read as a broken button;
 * 3. a raster is actually drawn — masking an empty pane is a rule with no subject, and it
 *    would take swipe away (below) from a reader who has no raster on.
 *
 * Swipe is the one this outranks: `setSwipe` refuses while it holds, and every write that can
 * make it hold releases swipe, exactly as `setAoiClip` does. Two writers on one pane's
 * `clip-path` is the §G6 problem this repeats the §G6 answer to.
 *
 * "A raster is drawn" is read from the R4 register (`registerRasterLayers`), which is the
 * store's only access to the §D5 catalogue. On a cold load it is empty until `/layers` lands,
 * so the rule simply does not bite yet — the same deliberate failure `toggleLayer`'s R4 guard
 * makes, and the mask itself does not depend on it (`features/map/ScopeClip` holds the
 * catalogue and reads the kinds directly).
 */
export function scopeMaskActive(
  s: Pick<AppState, 'scope' | 'country' | 'aoiClip' | 'layersOn'>,
): boolean {
  return filterGeographySet(s) && !s.aoiClip && activeRasterOf(s.layersOn) !== null;
}

/**
 * Would the mask hold the raster pane if swipe took it? — the question `setSwipe` asks, and
 * deliberately **not** `scopeMaskActive` of the state as it stands.
 *
 * Turning swipe on releases the AOI clip (§G6, `setSwipe` below), and the AOI clip is the one
 * thing holding the mask down. So `scopeMaskActive` read *before* the write answers about a
 * state the write is in the middle of destroying: a reader who filters, clips, then presses
 * Swipe passes a test that is already out of date and lands in exactly the state the
 * precedence exists to forbid — swipe and the mask both writing `rasterPane.clipPath`, the
 * winner decided by whichever map event fires last.
 *
 * So the question is asked of the state the write would produce. `aoiClip: false` is the only
 * field that write changes besides `swipe` itself, which makes this the whole of it.
 */
export function swipeBlockedByScope(
  s: Pick<AppState, 'scope' | 'country' | 'aoiClip' | 'layersOn'>,
): boolean {
  return scopeMaskActive({ ...s, aoiClip: false });
}

/**
 * §D1 `{id,name,role,org,country_scope}` — the wire shape of `/auth/login` and
 * `/auth/me`. Declared here rather than in `lib/api.ts` because `Role` is, and the
 * store consumes it; `lib/api.ts` re-exports it as part of its §D1 surface.
 * `id === null` ⇒ anonymous, and then `role` is `'public'`.
 */
export interface SessionUser {
  id: number | null;
  name: string | null;
  role: Role;
  org: string | null;
  country_scope: string | null;
}

/**
 * The signed-in half of §D1's user object. `role` is not repeated here: it is
 * `AppState.role`, which the §F3 demo switcher can move independently.
 */
export interface SessionIdentity {
  id: number;
  name: string;
  org: string | null;
  country_scope: string | null;
}

/* ----------------------------------------------- M4 §F5.6(e)/§F6 result layers */

/** The five shapes a §H tool's result can take on the map (`serializers.RESULT_LAYER_KINDS`). */
export const RESULT_LAYER_KINDS = ['units', 'choropleth', 'geojson', 'points', 'image'] as const;

export type ResultLayerKind = (typeof RESULT_LAYER_KINDS)[number];

/**
 * One entry of a §H tool's `result_layers` — the server's envelope, in server casing.
 *
 * Snake_case keys because that is what the API sends and this object is stored **as it
 * arrived**: the alternative is a camelCase translation layer whose only job is to be
 * updated whenever a tool grows a field, and the field a translation forgets is a layer
 * that silently paints nothing. The store's own bookkeeping (`ResultLayer`) is camelCase,
 * so the two are told apart at a glance.
 *
 * Which keys are populated depends on `kind`:
 * * `units` — `unit_ids` plus `colors` (unit id → the §H1 CMP colour of that row).
 * * `choropleth` — `values` (unit id → number; a unit with no value is **absent**, never
 *   null — see §H3), with `ramp`/`method`/`k` naming the §G classification and
 *   `unit_label` the units of the number.
 * * `geojson` / `points` — `uri`, the §D6 artefact link, plus an optional `color`.
 * * `image` — `url` (the §D6 artefact link, whole: the browser loads the PNG itself rather
 *   than this client fetching it), `bounds` as Leaflet's `[[south, west], [north, east]]`,
 *   and `legend`, the swatches block (e) prints under the row. The legend travels with the
 *   spec because a raster overlay has no values a client could re-classify — §H5's change
 *   image is coloured from the catalogue's own class table and nothing else knows it.
 */
export interface ResultLayerSpec {
  id: string;
  kind: ResultLayerKind;
  label: string;
  unit_ids?: string[];
  colors?: Record<string, string>;
  /**
   * `units` only — the units that must read as "no answer" rather than as a colour.
   *
   * UC1's launch round (design `2026-09-29-uc1-launch-design.md` §3) draws one constraint
   * at a time, and a constraint whose input is missing for a watershed is **not assessed**,
   * which is a third state beside flagged and not flagged. A third colour would be a third
   * class of the same kind; a hatch says "nothing was measured here" without competing with
   * the two real classes. The paint side is `resultUnits.ts::unitsPathStyle` (a
   * `unit-hatched` class over a light fill) and `ResultLayers`'s one injected
   * `<pattern id="intel-hatch">`.
   */
  patterns?: Record<string, 'hatched'>;
  /**
   * `units` only — an extra line under the unit name in the map tooltip, per unit. UC1's
   * constraint chips put the not-assessed reason here, which is design §3's "with the
   * reason in the legend and on hover".
   */
  details?: Record<string, string>;
  /**
   * `units` only — a thick outline in this colour, per unit, over whatever fill the unit
   * already has. UC2's launch round (design §3) draws the selected watershed this way on
   * **every** chip, so flipping chips keeps showing the same place; the `similar` chip
   * also outlines the reference in its own darker ink. The paint side is
   * `resultUnits.ts::unitsPathStyle`.
   */
  outlines?: Record<string, string>;
  /**
   * `units` only — draw the outlines and no fill at all. UC2's land-cover change chip
   * (2 Oct chart round) puts a raster on the map and still has to show which watershed is
   * selected: a filled unit over the raster would hide the very pixels the chip exists to
   * show, and an outline alone says the same thing without covering anything.
   */
  outlineOnly?: boolean;
  /**
   * `outlineOnly` only — every unit the layer names that carries **no** `outlines` entry
   * is drawn as a hairline boundary rather than a 3.4 px outline.
   *
   * Client round 2 Oct 2026. A raster chip takes the choropleth off the map on purpose,
   * which until now left nothing on the map to click: the hierarchy rows under it are
   * `fill: false` and NOTES #731 keeps them outline-only while a use case is open, so a
   * reader's click over the land-cover raster — the chip that is *about* where change
   * happened — reached nothing at all. The fix is one transparent, interactive layer
   * carrying **every** watershed at the current level, which is a hit target and a set of
   * boundaries and nothing else. Three and a half pixels times eleven would be a second
   * map drawn over the raster, so the unselected units are a hairline and the selected one
   * keeps the thick outline that makes it the selected one.
   */
  hairline?: boolean;
  /**
   * `units` only — the class breakdown the map legend prints under the result's name
   * (NOTES #729): one row per class with its colour (or a hatch) and how many units fall
   * in it. Without it the legend shows only the result's name. `n` is itself optional: a
   * class table that describes a raster palette rather than the units on screen (UC2's
   * land-cover chip) has no count to print.
   */
  classes?: { label: string; color: string; hatched?: boolean; n?: number }[];
  values?: Record<string, number>;
  ramp?: string;
  method?: 'quantile' | 'equal';
  k?: number;
  uri?: string;
  unit_label?: string;
  color?: string;
  /** `image` only — the artefact URL, and where on the earth to put it. */
  url?: string;
  bounds?: [[number, number], [number, number]];
  legend?: ResultLegendEntry[];
}

/** One swatch of an `image` result's own legend — §H5's "Changed to" rows. */
export interface ResultLegendEntry {
  value: number;
  label: string;
  color: string;
}

/**
 * A result layer as the store holds it: the server's spec plus who produced it and when.
 *
 * `jobId` is what the artefact fetch and the §F6 dock join on, and `tool` is what the
 * legend and any future grouping read; both are on the job rather than in the spec, so
 * they are recorded here at the moment the dock registers the layer rather than depended
 * on from a payload that does not carry them.
 */
export interface ResultLayer {
  /** `spec.id` — `{tool}:{job_id}`, so re-registering the same run replaces it in place. */
  id: string;
  jobId: string;
  tool: string;
  label: string;
  spec: ResultLayerSpec;
  createdAt: number;
}

/**
 * "Put this box in the frame" — see `AppState.fitRequest`. Leaflet's own `[[south, west],
 * [north, east]]`, which is exactly what an `image` result layer already carries.
 */
export interface FitRequest {
  bounds: [[number, number], [number, number]];
  /** Bumped on every request, so an identical box still moves the map. */
  seq: number;
}

/**
 * The programme extent as a `FitRequest`'s own `[[south, west], [north, east]]` — plan
 * Global Constraints (lon ∈ [-13, 16], lat ∈ [3.5, 25]), the same box
 * `features/map/panes.ts` holds as a Leaflet `LatLngBounds` under the name `HOME_BOUNDS`.
 *
 * Spelled here because `#intelReset` fits to it from the store, which must not import
 * Leaflet; `features/intelligence/uc2Constants.ts` re-exports this one rather than keeping
 * a third copy of four numbers that have to agree.
 */
export const PROGRAMME_BOUNDS: [[number, number], [number, number]] = [
  [3.5, -13],
  [24.5, 16],
];

/** What `addResultLayer` needs that the spec does not carry. */
export interface ResultLayerMeta {
  jobId: string;
  tool: string;
  /** Overrides `spec.label` — the dock may have a localised name for the run. */
  label?: string;
}

/**
 * Spec §F1, copied verbatim — **plus one field**, `user`.
 *
 * §F1's state object was written when the SPA had no session (NOTES #133: the role was
 * faked client-side). §D1 returns four more facts about the signed-in user — id, display
 * name, org and country scope — and the §F3 topbar needs the name for the avatar. They
 * are a small, additive superset of §F1: nothing in the spec's own list changes meaning,
 * and `user === null` is exactly the anonymous/`public` case §D1 defines. Logged as
 * NOTES #142.
 */
export interface AppState {
  role: Role;
  user: SessionIdentity | null;
  country: 'ALL' | 'BFA' | 'MLI' | 'NER' | 'NGA';
  /**
   * §F1 superset — the §F5.8 cascading filter (NOTES #389). `null` is the whole programme,
   * which is every state §F1 could describe; a non-null scope narrows *everything* the map
   * explorer draws and counts to one unit's subtree. See `Scope`.
   */
  scope: Scope | null;
  reportingLevel: ReportingLevel; // set ONLY via Operational hierarchy widget (§M3.5)
  /**
   * §F1 superset (NOTES #333). `'watershed'` is a *conditional* reporting mode: §M3.5 makes
   * the radio live only while the mapped indicator has native watershed values, so switching
   * to an indicator that has none must put the map back at an admin level. Which one? The
   * one the reader was last on — going to `1` from a communes view would silently re-scale
   * their map. Never `'watershed'` itself, so the fallback always terminates.
   */
  lastAdminLevel: 0 | 1 | 2;
  /**
   * Whether the reporting level on screen is one somebody **asked for**, rather than one the
   * cascade derived or the P1(b) snap borrowed. `yearPinned`, one field along, for the level.
   *
   * The snap moves the level onto a stratum the mapped indicator has data at. That is right
   * while that indicator is mapped and wrong the moment it is not: a reader who opened a
   * 2025 baseline series (countries, because regions hold nothing) and then went back to
   * `b21` was left on the four-polygon country map, because `snapLevelToData` was asked to
   * snap *from the previous snap's answer* and countries is a level `b21` also has. Nothing
   * on screen could undo it — the chooser is refused for roll-up indicators by design, and
   * "Clear filter" does nothing when no filter is set — so the only way back to R5's regions
   * was a reload.
   *
   * So the snap now starts from `cascadeLevel` rather than from the level in the store, and
   * this flag is what protects the two levels a *person* can state:
   *
   * * the `<LevelChooser>` pick — `setReportingLevel`, the control's only writer;
   * * a permalink's `lv=` — `permalink.applyToStore`, exactly where it sets `yearPinned`.
   *
   * Cleared by the two writes that mean "start the geography again" (`clearScope`, and the
   * `setCountry` that drops a scope belonging elsewhere). **Not** cleared by `setIndicator`:
   * the sibling bars map a series with one click, and dropping the reader off the watershed
   * view they chose every time they compared two practices would be the same one-way trip
   * from the other end.
   *
   * Not in the permalink itself: `lv=` is serialised whenever the level is not the default,
   * and a link that states a level restores it pinned by construction.
   */
  levelPinned: boolean;
  indicator: string | null; // base (fill) indicator id
  overlays: { id: string; color?: string }[]; // max 2
  biv: { on: boolean; y: string | null };
  yearIdx: number;
  years: number[];
  /**
   * Whether the year on the slider is the reader's, rather than a default nobody chose.
   *
   * A cold load has no opinion about the year, so it opens on the catalogue's
   * `default_year` for the mapped indicator (§D3 — the latest *substantially reported*
   * year) as soon as `/indicators` answers. That arrival is asynchronous, and it must never
   * yank the slider away from a year the reader has already expressed: a permalink `y=`
   * and any move of the slider, the ticks or the play button set this, and
   * `applyDefaultYear` becomes a no-op from then on.
   */
  yearPinned: boolean;
  /**
   * Has the *reader* chosen a year — the time bar, the year select, or a permalink's
   * `lv=` — as opposed to the boot default `applyDefaultYear` pins? `yearPinned` cannot
   * tell the two apart (the boot default pins too, so a later catalogue refetch does not
   * move it), and the explorer's activity squares must only narrow by year when a person
   * asked (client, 2026-09-23): the boot year hid 234 of 237 categorised activities.
   */
  yearChosen: boolean;
  /**
   * §F5.10 superset (NOTES #361). How the time bar reads a year: `false` (the default) is
   * the annual series — what happened *in* the selected year — and `true` is the running
   * total to that year, `?mode=cumulative` on §D3's values/series endpoints.
   *
   * It exists because the stored series became annual: MELS' B.2.1 is published as a
   * programme-to-date figure, the sync used to store *that* at every year, and the time bar
   * therefore animated a curve that could only climb. The accumulation is a legitimate and
   * wanted reading — it is the number on MELS' own dashboard — so it is a reader-facing
   * switch rather than a decision baked into the data.
   *
   * A **global** flag, not a per-indicator one: it is a way of reading the map, and it
   * applies to the fill, the overlays, the profile and the dock at once, exactly like
   * `year`. Indicators that do not accumulate (`is_cumulative: false`) ignore it — the
   * checkbox is hidden for them and no `mode=` is sent (`modeFor`, lib/api.ts).
   */
  cumulative: boolean;
  layersOn: Set<string>;
  layerOpacity: Record<string, number>;
  /**
   * Task 6: the drawn epoch of every **date series** currently on the map, keyed by the
   * series' own id (`ndvi_terra` → `'2026-09-06'`) — the store's half of `drawnEpochOf`.
   * Absent for a series nobody has picked an epoch of yet, which reads as "the newest".
   * Entries for a series that leaves `layersOn` are dropped by `setActiveRaster`, so a stale
   * date can never survive to be read back once the series it was about is gone.
   */
  epochOn: Record<string, string>;
  /**
   * Task 6: the crop-mask layer (`mask_maize`) stencilled over the drawn NDVI series, or
   * `null` for none. Meaningless — and cleared — the moment the drawn raster is not one of
   * `NDVI_SERIES` (`setActiveRaster`), so it can never survive onto an unrelated raster.
   */
  ndviMask: string | null;
  indStyle: { ramp: string | null; method: 'quantile' | 'equal'; k: number; opacity: number };
  legendMode: 'values' | 'classes';
  domFilter: string;
  layerQuery: string;
  basemap: Basemap;
  /**
   * Whether the basemap is one somebody *stated*, rather than the value a cold boot starts on.
   *
   * The same idea as `levelPinned`, for the same reason: `/meta/config.basemaps.default` is
   * this deployment's cold-boot basemap and it arrives *after* the first render, so the store
   * has to be able to tell "still on the boot value" from "the reader picked this". A `bm=`
   * link pins it (`applyToStore`) and so does the panel (`setBasemap`), and
   * `adoptDefaultBasemap` never overrides either.
   *
   * Not in the permalink: `serialize` always emits `bm=`, so a restored link is pinned by
   * construction.
   */
  basemapPinned: boolean;
  /**
   * §F5.10's drawn ring, its §G7 area — and its `lib/aoi.ts` identity (M4).
   *
   * `id` is `ringId(pts)` and is maintained by `setAoi`, so it can never disagree with the
   * ring it names. See `lib/aoi.ts` for why a ring needs one at all.
   */
  aoi: { pts: [number, number][]; areaKm2: number; id: string } | null;
  aoiClip: boolean;
  aoiFocus: string | null;
  /**
   * §F5.6(e)/§F6: the analysis results currently drawn on the map, oldest first.
   *
   * **Unbounded, and deliberately.** Every other collection in this store has a cap
   * (`MAX_OVERLAYS`, one raster) because two of the same thing fight over the same paint.
   * Result layers do not: they are separate answers to separate questions, and a reader
   * comparing four of them is using the tool as intended. The §F5.6 block header carries
   * the count, and its block-level ✕ is how the list gets short again.
   *
   * **Not cleared by the sidebar tab switch, the country or the scope** — see
   * `switchSidebarTab` and `setCountry`.
   */
  results: ResultLayer[];
  /**
   * A view move a panel has asked for, which only the map can carry out.
   *
   * The §F6 dock lives outside the Leaflet tree, so it has no `useMap` and no business
   * acquiring one: "Show change on map" is a *request* — bounds plus a counter — and
   * `ResultLayers` is what turns it into a `fitTo`. The counter is what makes the second
   * press of the same button move the map again: the bounds have not changed, so without
   * it the consuming effect would have nothing to re-run on.
   */
  fitRequest: FitRequest | null;
  /**
   * How many times something has asked for the **default view** to be opened again —
   * client round 2 Oct 2026, `#intelReset`'s "back to the map I started on".
   *
   * `DefaultView` is a once-per-page-load write (`features/map/defaultViewOnce.ts` says
   * why the flag is module-level rather than a ref). Reset has to undo that "once": the
   * reader is asking for the boot map back, not for an empty one. A counter rather than a
   * boolean, for the same reason `fitRequest` carries a seq — a second Reset is a second
   * request, and a flag that is already `true` would have nothing to re-run on.
   */
  defaultViewSeq: number;
  swipe: boolean;
  /**
   * UC2 launch round (client spec `2026-10-01-UC2_LAUNCH_SPEC.md` §2, "Land-cover change"):
   * the two **already-published** rasters the swipe handle compares, `left` and `right`.
   *
   * Plain swipe clips the thematic panes against the basemap — one layer against what is
   * under it. A land-cover comparison needs two layers at once, which the §F5.2 "one raster
   * at a time" rule (`registerRasterLayers`) rightly refuses for the Layers pane. So the
   * pair travels in its own field and is drawn by `RasterLayers` into its own two panes
   * (`right` into `rasterPane`, which the handle already clips; `left` into `swipeLeftPane`,
   * which the same handle clips the other way) — `layersOn` is untouched and the reader's
   * own raster pick survives the trip.
   *
   * Setting a pair switches swipe **on**; clearing it (or switching swipe off by hand, or
   * resetting the use case) switches swipe off and removes both rasters, in one write.
   */
  swipePair: { left: string; right: string } | null;
  selectedUnit: string | null;
  /**
   * Amendment 1 §useUnitLink: the Leaflet `latlng` of the click that last set
   * `selectedUnit` through the map (`HierarchyLayers`/`Choropleth`), `[lat, lon]`. Not
   * written by `SearchBox` or a pane pick — those set `selectedUnit` to a unit already
   * known to be a watershed or a country, which needs no point to resolve against. A map
   * click on a commune or region is the one case `useUnitLink` cannot answer from the unit
   * alone: it has to test a *point* against the watershed polygons, and the point a reader
   * actually clicked is truer than that unit's centroid (the fallback for a unit reached
   * some other way, e.g. `SearchBox`, while a use case is active).
   */
  lastClickLatLng: [number, number] | null;
  dockInd: string | null;
  /**
   * §F6: whether the analysis dock is on screen.
   *
   * Store state rather than `MapTools`' own `useState`, because two controls open it —
   * `#btnAnalysis` in §F5.10's third group and the §F3 topbar's "Analysis" item, which is
   * an *action on `/`* rather than a route (`app/nav.ts`) — and a panel with two switches
   * and one of them private is a panel that can disagree with itself.
   *
   * Deliberately **not** in the permalink: a shared link is a view of the map, and the
   * receiving reader has drawn no AOI and picked no units, so a dock restored mid-form
   * would be a form about nothing.
   */
  analysisOpen: boolean;
  /**
   * Client directive (2026-09-15): the year/epoch sliders are now behind an **Animate**
   * map tool (`#btnAnimate`) rather than always-on bars. `TimeBar` and `EpochSlider` render
   * `null` unless this is set, in addition to their existing conditions — the drop-downs
   * beside the data (`#yearSelect`, `#epochSelect`) are the everyday control and do not
   * depend on it.
   *
   * Not in the permalink: like `analysisOpen`, a shared link is a view of the map and a
   * bar left open mid-animation is not part of that view.
   */
  animateOpen: boolean;
  splashOpen: boolean;
  /**
   * Client review R1: the sign-in gate, the geoportal's **first** page.
   *
   * Ahead of `splashOpen` in the sequence, not beside it — while this is true the splash
   * does not render and does not fetch, so a session goes gate → splash → app. The fields
   * behind it are inert (permissions come later); dismissing it is the whole of its
   * contract, which is why one boolean is all the state it needs.
   */
  gateOpen: boolean;
  /**
   * Client item 6: the §F8 dataset record the reader has open, by dataset id — `null` when
   * none is.
   *
   * App state rather than the catalogue page's own `useState`, because the record is now
   * asked for from two places that share no component tree: a `.ds-card` on `/catalogue`
   * and the "View metadata" link on an active layer row in the §F5.2 sidebar, which lives
   * over the map. One field, and `app/RecordHost.tsx` draws it once as a sibling of the
   * routes — so opening a record from the map is an overlay, not a navigation: nothing
   * unmounts, and the map is exactly as it was when the modal closes.
   *
   * Deliberately **not** in the permalink: a shared link is a view of the map, and §F8 has
   * its own addressable page for a dataset the sender wants to point at.
   */
  recordId: string | null;
  tourStep: number; // -1 = off
  /**
   * §F1 superset (NOTES #185). §F5.2 specifies the sidebar as "332px, collapsible; body
   * class `sb-open` drives dependent widget offsets" but §F1's state object has no field
   * for it (the mockup kept it in the DOM, toggling `#sidebar.collapsed` directly). It is
   * app state — the §F5.10 map tools and the M3 permalink both need to read it — so it
   * lives here. Default `true`: the pane is open on first load, as in the mockup.
   */
  sidebarOpen: boolean;
  /**
   * Client round, 3 October 2026: was the sidebar collapsed *by the analysis dock*, rather
   * than by the reader?
   *
   * The dock and the sidebar between them leave about 330 px of map on a laptop, so
   * opening the dock collapses the pane (`setAnalysisOpen`). Giving it back when the dock
   * closes needs this one bit of memory, because the two readers it has to tell apart look
   * identical at the moment the dock closes — both have `sidebarOpen: false`:
   *
   * * the dock collapsed it, and the reader expects their pane back → re-open;
   * * the reader had already collapsed it themselves (or collapsed it again while the dock
   *   was open), and re-opening would be the app undoing their own click → leave it.
   *
   * Cleared the moment the reader opens the pane by hand (`setSidebarOpen(true)`): from
   * then on the sidebar is theirs and the dock has nothing to give back. Session-only and
   * not in the permalink, like `sidebarOpen` and `analysisOpen` themselves.
   */
  sidebarAutoHidden: boolean;
  /**
   * §F7 dashboard: the domain the dashboard's own Filters card has picked, local to that
   * card — `null` is "every domain". Not the map's `domFilter`: the dashboard's KPI grid,
   * targets table and coverage rows filter by domain the same way the map does, but the
   * two pages are asking different questions of different data, and one field driving both
   * would mean opening the dashboard on a domain the reader picked for the *map* (or the
   * reverse) rather than the domain they actually chose for this page.
   *
   * Session-local, **not** in the permalink — see `dashYear`, immediately below, for why.
   */
  dashDomain: string | null;
  /**
   * §F7 dashboard: the year the dashboard's own Filters card has picked, local to that card
   * — `null` defers to the map's `selectYear` (`resolveDashYear`, `features/dashboard`).
   *
   * Session-local, **not** in the permalink. §F1's permalink (`lib/permalink.ts`) is the
   * *map's* state — one URL that restores the Map Explorer's view, filters and geography —
   * and the dashboard is a different page with its own Filters card, not a mode of the map.
   * Folding `dashYear`/`dashDomain` into `#y=`/`#dom=` would make a shared map link silently
   * carry an opinion about a page it was never about, and a shared dashboard view is not a
   * requirement this milestone's spec asks for (§1.1 lists no "share" affordance for it).
   */
  dashYear: number | null;
  /**
   * Task 6 §6.3: the Intelligence pane's own local state — which use case the `#intelSelect`
   * dropdown has open, the selected unit (map click or the pane's own list), UC2's candidate
   * set and comparison pick, and UC3's stepper position.
   *
   * `useCase: null` (or `'tools'`, the dropdown's own last option) means the dock body is
   * today's `ToolHome`/tool views, unchanged — nothing here is read until a use case is
   * actually chosen. **Not in the permalink** (§6.3: "none of it is in the permalink — the
   * dock has never been"), for the same reason `analysisOpen` and `selectedUnit` are not:
   * a shared map link is a view of the map, not a form a stranger should reopen mid-pick.
   */
  intel: IntelState;
}

/**
 * Amendment 1 §A/§1: the pane's three square tabs. `usecases` is the default — a cold dock
 * opens on "Choose analysis", not on the toolbox or Help.
 */
export type IntelTab = 'usecases' | 'tools' | 'help';

/** See `AppState.intel`. */
export interface IntelState {
  useCase: string | null;
  unit: string | null;
  candidates: string[];
  compareWith: string | null;
  step: number;
  /**
   * Amendment 1 §1: which of the three tabs is showing. Lives beside the rest of `intel`
   * rather than as `AnalysisDock`'s own `useState` because Help's "Take me there" has to be
   * able to set it from a sibling panel, and because a reopened dock returns to the same
   * tab (`setAnalysisOpen(false)` keeps it) while `resetIntel` and a fresh `#intelSelect`
   * pick leave it alone too — none of the three is "start the tab over".
   */
  tab: IntelTab;
  /**
   * Design doc `2026-09-28-intel-subtabs-design.md` §1: which second-level tab is showing
   * inside the UC1/UC2 result panes (`#uc1Tabs`/`#uc2Tabs`) — `'overview'`, `'indicators'`,
   * `'why'`, `'method'` for uc1, or `'overview'`, `'trends'`, `'practices'`, `'similar'`,
   * `'method'` for uc2. Not validated against either list here — `SubTabs` falls back to its
   * own first tab when the stored value is not one of its own, so switching use case never
   * needs to reset this on its own. A fresh `#intelSelect` pick and `#intelReset` both return
   * it to `'overview'` because both spread `INITIAL_INTEL_STATE`. Not carried by the
   * permalink (`KEY_ORDER` unchanged).
   */
  subTab: string;
  /**
   * Amendment 1 §A: the My Analysis tab's own tool switch — `ToolHome`'s six cards, or the
   * one currently open. Lifted out of `AnalysisDock`'s local `useState` (Task 6) into the
   * store for the same reason `tab` is: a Help card's "Take me there" targeting `{tool}`
   * has to be able to open it from outside the dock's own component tree. `null` is
   * `ToolHome`. Preserved across an `#intelSelect` pick and a `resetIntel` (a use case and
   * a half-configured tool are two unrelated tabs' business); reset when the dock closes,
   * same as before it was lifted.
   */
  dockTool: ToolId | null;
  /**
   * UC2 launch round (client spec `2026-10-01-UC2_LAUNCH_SPEC.md` §2/§3): which of the
   * seven exclusive chips (`#uc2Chip-*`) the UC2 map is drawn from — `'pattern'` by
   * default — and which practice group (`#uc2Practice`) the practices chip, the practices
   * chart and the similar strip all read. Held here for the reason `uc1Chip` is: the chip
   * decides what `intel:uc2:choropleth` paints, and that effect lives in `Uc2.tsx` one
   * level up from the chips. Both reset with the slice (`INITIAL_INTEL_STATE`), so a new
   * use case and `#intelReset` return the map to Pattern/agroforestry. Neither is carried
   * by the permalink (`KEY_ORDER` unchanged).
   */
  uc2Chip: string;
  uc2Practice: string;
  /**
   * UC1 launch round (design `2026-09-29-uc1-launch-design.md` §3): which of the six
   * exclusive constraint chips (`#uc1Chip-*`) the map is drawn from — `'priority'` (the
   * three-class category map) or one of the five dimension ids. Held here rather than in
   * `ConstraintChips`'s own `useState` because the chip decides what
   * `intel:uc1:choropleth` paints, and that effect lives in `Uc1.tsx` one level up. Reset
   * with the slice (`INITIAL_INTEL_STATE`), so a new use case and `#intelReset` both return
   * the map to Priority. Not carried by the permalink (`KEY_ORDER` unchanged).
   */
  uc1Chip: string;
  /**
   * Fix round 1: the layer/raster ids some `intel.*` control has switched on — today any
   * `#intelUnderlying` toggle — so `#intelReset` (and
   * a use-case switch) can turn off exactly what the pane itself turned on, without touching
   * a layer the reader had already switched on through the ordinary Layers pane. An id is
   * added when an intel control switches it on and removed when that same control (or a
   * reset) switches it back off; it is not a mirror of `layersOn`/`activeRasterOf`, which can
   * hold ids no intel control has any opinion about.
   */
  layersToggled: string[];
}

/** `AppState.intel`'s resting shape — every reset (`setIntelUseCase`, `resetIntel`) returns here. */
export const INITIAL_INTEL_STATE: IntelState = {
  useCase: null,
  unit: null,
  candidates: [],
  compareWith: null,
  step: 0,
  tab: 'usecases',
  subTab: 'overview',
  dockTool: null,
  uc1Chip: 'priority',
  // `UC2_CHIPS[0]` and `UC2_DEFAULT_PRACTICE` — spelled out rather than imported, so this
  // module keeps depending on nothing under `features/`.
  uc2Chip: 'pattern',
  uc2Practice: 'agroforestry',
  layersToggled: [],
};

/** Actions implemented in M1. Later milestones add to this interface, not to AppState. */
export interface AppActions {
  setCountry: (country: Country) => void;
  /**
   * §F5.8 cascade: filter the whole map to one unit's subtree.
   *
   * Two invariants it owns, so no caller can break them by calling in the wrong order:
   *
   * * **the global country follows the scope.** A scope inside Burkina Faso *is* a choice of
   *   country, and leaving the topbar on "All countries" while the map shows one region
   *   would put two geography controls on screen disagreeing with each other.
   * * **the reporting level stays inside the scope** (`levelWithinScope`) — a region scope
   *   paints communes, and a level the subtree has no units at would be an empty map with
   *   the radios still claiming otherwise.
   */
  setScope: (scope: Scope) => void;
  /**
   * §F5.8 "Clear filter" — **back to the first operational level** (client correction D3).
   *
   * Not "undo the last dropdown": the whole geography goes back to what a cold load opens
   * on — no scope, regions, *and* `country: 'ALL'`. IFDC's reading is that a reader who
   * clears a filter is starting again, and leaving the country selector on Burkina Faso
   * after "Clear filter" left them looking at a filtered map with nothing in the cascade
   * saying so. The map's own fit follows from the two fields (`GeographyFocus`): scope
   * `null` + country `ALL` is the programme extent.
   */
  clearScope: () => void;
  setReportingLevel: (level: ReportingLevel) => void;
  setBasemap: (basemap: Basemap) => void;
  /**
   * §F5.1: adopt `/meta/config.basemaps.default` — the deployment's cold-boot basemap.
   *
   * A no-op once anything has *stated* a basemap (`basemapPinned`): the reader's pick and a
   * `bm=` link both outrank a default, and the config arrives after both. See `basemapPinned`.
   */
  adoptDefaultBasemap: (basemap: Basemap) => void;
  /**
   * §F3/§I2: the demo switcher's client-only role change. Moves the gating, never the
   * identity — the server still sees whatever session the cookie carries.
   */
  setRole: (role: Role) => void;
  /** §D1 `GET /auth/me` landed: adopt both the identity and the authoritative role. */
  setSession: (user: SessionUser) => void;

  /* -------------------------------------------------- M2 §F5.2/§F5.3 indicator state */

  /**
   * §F5.2: "Item click: single mode → set/unset base indicator (reset indStyle.ramp …)".
   * The ramp reset is the point — a ramp the user picked for a rainfall anomaly must not
   * survive onto a producer count, whose §G2 auto ramp is a completely different family.
   *
   * `available` (P1(b)) is `effectiveLevels` of the indicator being mapped: the level moves
   * to one this indicator has data at, in the same write, so the map's *first* request is
   * the right one. Omitting it is the old behaviour exactly — an empty list never snaps.
   */
  setIndicator: (
    id: string | null,
    hasWatershedValues?: boolean,
    available?: readonly ReportingLevel[],
  ) => void;
  /**
   * §M3.5 invariant, for the paths that do not go through `setIndicator`: a permalink
   * hydrated straight into `setState`, or `PermalinkSync` dropping an indicator a stale link
   * named. Demotes `'watershed'` to `lastAdminLevel` when the mapped indicator turns out not
   * to have native watershed values; a no-op at every admin level.
   *
   * Callers must only pass `true` once `/indicators` has actually answered — reconciling
   * against an empty catalogue would demote every restored watershed link on boot.
   *
   * **`indicatorId` is which indicator the answer is about, and it is load-bearing** (R6 fix).
   * Two effects call this off the same `/indicators` landing — `PermalinkSync`, after it has
   * corrected the indicator a stale link named, and `OperationalHierarchy`, from the render
   * that happened *before* that correction. The demotion is one-way (there is no path back up
   * to `'watershed'`), so the effect holding the stale indicator permanently lost a restored
   * `lv=watershed`, depending on which one React flushed first. Naming the indicator makes the
   * call self-invalidating: an answer about an indicator that is no longer mapped is an answer
   * to a question nobody is asking any more, and is dropped.
   *
   * **`available` is client observation P1(b)** — `effectiveLevels` of the mapped indicator.
   * The §M3.5 demotion is one direction of the same idea (a level the indicator has no data
   * at is not a level to map it at); this is the general form, and it is what makes the 2025
   * baseline paint at all. It runs *after* the demotion, on the level the demotion left, so
   * the two can never fight. An empty list — the catalogue has not answered — is a no-op,
   * exactly as `watershedSupported` is only trusted once `/indicators` has landed. It is also
   * how a **geography** change reaches the level: `setScope`/`setCountry` cannot see the
   * catalogue, so the widget re-runs this on every scope and country change and the snap
   * lands against the level the cascade has just derived. See `snapLevelToData`.
   */
  reconcileReportingLevel: (
    indicatorId: string | null,
    watershedSupported: boolean,
    available?: readonly ReportingLevel[],
  ) => void;
  /** §F5.6 style panel: patch one or more of `{ramp, method, k, opacity}` live. */
  setIndStyle: (patch: Partial<AppState['indStyle']>) => void;
  /**
   * §F5.10 time bar. Clamped to `years`, so a stale index can never blank the map.
   * Also **pins** the year: from here on the reader owns it (see `yearPinned`).
   */
  setYearIdx: (idx: number) => void;
  /**
   * Open on the catalogue's `default_year` for the mapped indicator — once, and only while
   * nothing has pinned the year (`yearPinned`). `null` — an indicator with no values at
   * all — pins nothing and leaves the slider where it is, so a later indicator with a real
   * default still gets its turn.
   */
  applyDefaultYear: (year: number | null | undefined) => void;
  /**
   * Move off a year the indicator has no data for — the year counterpart of
   * `reconcileReportingLevel`. `preferred` (the catalogue's `default_year`) wins when it is
   * one of the available years; otherwise the newest available one does. An empty list is a
   * no-op: "the catalogue has not answered yet" must not be read as "this indicator has no
   * data".
   */
  snapYearToData: (available: number[], preferred?: number | null) => void;
  /**
   * §F5.10's "Cumulative" checkbox. Purely a *reading* of the same stored data — nothing
   * about the map's geography, indicator or year changes, so nothing else is reset.
   */
  setCumulative: (on: boolean) => void;
  /** §F5.6 legend `Values | Low – High` switch — labelling only, never the paint. */
  setLegendMode: (mode: AppState['legendMode']) => void;
  /** §F5.2 domain chip grid — filters the indicator list only, never the map. */
  setDomFilter: (domain: string) => void;
  /** §F5.3 click → §F5.7 unit profile. */
  setSelectedUnit: (id: string | null) => void;
  /** Amendment 1 §useUnitLink: the map click that set `selectedUnit`, for point-in-watershed. */
  setLastClickLatLng: (latlng: [number, number] | null) => void;
  /** §F5.2 sidebar collapse; also drives the `sb-open` body class. */
  setSidebarOpen: (open: boolean) => void;
  /**
   * **Client round 4 E2**: moving between the two pane tabs clears the other side.
   *
   * > *"Switching to Layers clears the mapped indicator (fill, overlays, bivariate).
   * > Switching to Indicators clears active layers (the raster pick and vector overlays)."*
   *
   * The two panes had become two ways to paint the same map at once, and the reader who went
   * to switch a layer on came back to a choropleth they had stopped looking at under a raster
   * they could not see through. IFDC's resolution is that a tab is a *mode*: what the map
   * shows is what the open pane is about.
   *
   * **One action rather than a clearance per consumer**, because the clearance has five
   * audiences — the legend blocks, the sidebar analytics, the permalink, the status bar and
   * the map itself — and every one of them already reads `indicator`/`overlays`/`biv`/
   * `layersOn`. Writing those fields once is what makes all five agree without any of them
   * knowing a tab exists.
   *
   * Silent, like every other clearance in this file (`setMapMode`, `addOverlay`'s cap): the
   * *message* wants `t()`, so `Sidebar` raises it and only for the first switch in each
   * direction (`features/sidebar/paneSwitch.ts`).
   *
   * **Interactive switches only.** A permalink restores both sides as they were saved — a
   * shared link is a record of a map, not a click — so `applyToStore` and `PermalinkSync` do
   * not go through here and a link naming `ind=` and `ly=` still opens with both. It is the
   * tab that is exclusive, not the state.
   *
   * **`results` are not part of the mode and are never cleared here (M4).** The tab decides
   * what the *explorer* paints — an indicator or a thematic layer — and an analysis result
   * is neither: it is an answer the reader asked for and waited for, registered in legend
   * block (e) with its own ✕. Throwing one away because they went to the Layers pane to see
   * what the result sits on would be the worst clearance in this file. `state.test.ts`
   * asserts it, so a future clearance cannot slip in with a new field.
   *
   * The §M3.5 demotion `setIndicator(null)` performs is deliberately **not** repeated here:
   * since R5 the reporting level is a consequence of the cascade, and with nothing mapped
   * nothing asks §D3 for values at all. The next `setIndicator` reconciles the level against
   * the indicator that is actually mapped, which is the only moment the answer can be right.
   */
  switchSidebarTab: (tab: SidebarTab) => void;

  /* ------------------------------------------------------------ M7 §F4 splash */

  /**
   * Open or close the §F4 splash overlay. **Closing marks the session**, so the reader is
   * not shown the landing page again on the next route change or reload — the "Overview"
   * nav button is how they ask for it back. Reopening deliberately does *not* unmark it:
   * asking to see the page again is not asking to be shown it again unprompted.
   */
  setSplashOpen: (open: boolean) => void;

  /**
   * Open or close the §F6 analysis dock. Opening also drops the selected unit, because
   * the §F5.7 profile and the dock occupy the same right-hand column (see `analysisOpen`).
   */
  setAnalysisOpen: (open: boolean) => void;

  /** Toggle the Animate map tool — see `animateOpen`. */
  setAnimateOpen: (open: boolean) => void;

  /* ---------------------------------------------------- client review R1 sign-in gate */

  /**
   * Close (or reopen) the R1 sign-in gate. **Closing marks the session**, exactly like the
   * splash — both of the gate's buttons call this with `false` and nothing else, because
   * both of them mean the same thing to the app today: the reader is in.
   */
  setGateOpen: (open: boolean) => void;

  /* ------------------------------------------------- client item 6: the §F8 record overlay */

  /** Open the §F8 dataset record for `id` over whatever page is showing — see `recordId`. */
  openRecord: (id: string) => void;

  /** Close it. The page underneath never moved, so there is nothing to restore. */
  closeRecord: () => void;

  /* --------------------------------------------------- M3 §F5.2 thematic layers */

  /**
   * §F5.2 Layers pane checkbox **and** §F5.6 block (d)'s ✕ — one action, so the two
   * controls can never disagree about what is drawn. `on` omitted = flip.
   *
   * `layersOn` is a `Set`, and zustand compares by reference: mutating it in place would
   * leave every subscriber unnotified, so a new Set is always constructed.
   *
   * **Client review R4 lives here: at most one raster is ever on.** Switching a second one
   * on is refused — *silently*, for the same reason `addOverlay`'s cap is: R4 asks for a
   * tooltip and a message, and a store action has no `t()`. The callers read
   * `selectActiveRaster` first, disable the row and explain; this is the guarantee for every
   * caller that switches layers on **one at a time**. It is not, on its own, the guarantee for
   * the whole invariant: a permalink assigns `layersOn` wholesale before the catalogue that
   * says which ids are rasters even exists. That half is `registerRasterLayers`, which
   * normalises the set the moment the rule becomes evaluable. Between them there is no writer
   * — a legend block, a permalink, a future tool — that can leave two tile layers stacked
   * opaquely on top of each other. Vectors are untouched and combine freely.
   */
  toggleLayer: (id: string, on?: boolean) => void;
  /**
   * §F5.2 *Raster layer* `<select>` — client correction D2's "one visible raster at a time,
   * **the single pick IS the rule**". `null` is the select's *None* option.
   *
   * The difference from `toggleLayer` is the whole point of the control: a checkbox that
   * refused the second raster was the only honest shape for a list of tick boxes, but a
   * listbox has exactly one value, so picking a second raster **replaces** the first rather
   * than being turned down. Every raster leaves `layersOn` and the pick (if any) goes in, in
   * one write — so there is no intermediate state in which two are on and no order of two
   * `toggleLayer` calls to get wrong. Vectors in the set are untouched.
   *
   * `rasterIds` is *which ids are rasters*, and the caller may pass it because the caller is
   * the one holding the §D5 catalogue. It defaults to the register (`registerRasterLayers`),
   * which is the same list arriving by a different route — but the register is written by
   * whoever holds the **map's** copy of that query, and a pane that could only clear rasters
   * once some other component had mounted would have a *None* option that silently did
   * nothing. A control must not depend on a component it does not contain.
   */
  setActiveRaster: (id: string | null, rasterIds?: Iterable<string>) => void;
  /** §F5.2 per-layer opacity slider, live (0–1). */
  setLayerOpacity: (id: string, opacity: number) => void;
  /** §F5.2 *Filter layers* input — narrows the tree only, never the map. */
  setLayerQuery: (query: string) => void;

  /* --------------------------------------------------- Task 6: dated raster epochs */

  /**
   * Task 7's epoch control writes here: `date` picks that date series' drawn epoch,
   * `null` deletes the key (back to "newest", `drawnEpochOf`'s default).
   */
  setEpoch: (id: string, date: string | null) => void;
  /** Task 7's mask picker: which crop mask (if any) stencils the drawn NDVI series. */
  setNdviMask: (id: string | null) => void;

  /* ------------------------------------------------ M3 §F5.5 overlay indicators */

  /**
   * §F5.2's `+` button. Appends `{id}` (no colour: §G4 picks one against the fill ramp).
   *
   * **Capped at `MAX_OVERLAYS`, silently.** §F5.5's "Max 2 overlays (toast)" needs a
   * *message*, and a store action is the wrong place to raise one — it has no `t()` and no
   * view. So the cap lives here as the guarantee that state can never hold three, and the
   * caller checks `overlays.length` first to decide whether to toast. Re-adding an id
   * already on the list is a no-op rather than a duplicate, which is what makes the button
   * idempotent under a double click.
   */
  addOverlay: (id: string) => void;
  /** §F5.2 `+`-again and §F5.6(c) ✕ — one action, so the two controls stay in sync. */
  removeOverlay: (id: string) => void;
  /**
   * §F5.6(c) "recolour:" swatches. `undefined` **clears** the override and hands the
   * overlay back to §G4's auto-contrast pick — which is what clicking the already-selected
   * swatch does.
   */
  setOverlayColor: (id: string, color: string | undefined) => void;
  /** §F5.4: "On enter: clear overlays (toast why)". Also §F5.11's mutual exclusion. */
  clearOverlays: () => void;

  /* ---------------------------------------------------- M3 §F5.4 bivariate mode */

  /**
   * §F5.2's *Map mode* segment. Entering bivariate **keeps the base indicator as X** and
   * empties `overlays` — §F5.11's "bivariate excludes overlays and vice versa" — and
   * leaving it drops the Y pick, so the two modes can never share a half-state.
   *
   * The overlay clearance is silent here for the same reason `addOverlay`'s cap is (see
   * above): §F5.4 wants a toast *explaining why*, and a store action has no `t()`. The
   * caller reads `overlays.length` before switching and raises the message.
   */
  setMapMode: (mode: MapMode) => void;
  /**
   * §F5.4's Y axis — the second indicator, picked by clicking a row while bivariate is on.
   * A no-op outside bivariate mode: `biv.y` without `biv.on` is a state the renderer, the
   * legend and the sidebar highlight all read as "no bivariate", and letting it be written
   * would leave a stale Y waiting to reappear the next time the mode is switched on.
   */
  setBivY: (id: string | null) => void;

  /* -------------------------------------------------- M3 §F5.9 indicator dock */

  /**
   * §F5.9: "Click toggles popover". One action for both the dock button and the popover's
   * ✕ — clicking the *active* button closes the panel it opened, which is what makes the
   * dock a set of toggles rather than six ways to open the same widget.
   *
   * Deliberately **independent of `indicator`**: the popover describes an indicator without
   * mapping it (that is its "Map this indicator" button), so a reader can compare the
   * programme total for C.2.1 while B.2.1 is painted on the map.
   */
  toggleDockInd: (id: string) => void;
  /** §F5.9 ✕ / an explicit open (`null` closes). */
  setDockInd: (id: string | null) => void;

  /* ------------------------------------------ M3 §F5.10/§G6 AOI clip & swipe */

  /**
   * §F5.10 draw-AOI: the finished (or in-progress ≥3 vertices) ring plus its §G7 area.
   *
   * `null` **releases the clip**, because a clip with no polygon is a mask over nothing —
   * §F5.10's "re-click clears + releases clip" in one action, so no caller can leave
   * `aoiClip` true with `aoi` null and blank the thematic panes.
   *
   * **The caller does not supply `id`** — `setAoi` computes it from the ring (M4), so the
   * identity every §H2/§H3 request and the §F6 dock key on is a *function* of the polygon
   * rather than a second field two writers could set differently.
   */
  setAoi: (aoi: { pts: [number, number][]; areaKm2: number } | null) => void;
  /**
   * §G6 "mutually exclusive": turning the clip on turns swipe off. Silent — §G6 wants a
   * toast *explaining* the pause, and a store action has no `t()`; the caller reads the
   * previous `swipe` and raises the message. The invariant lives here so the two can never
   * both own `clip-path` on `rasterPane`/`choroPane`.
   *
   * Refuses to switch on with no `aoi` (see `setAoi`).
   *
   * There is deliberately no `toggleAoiClip`/`toggleSwipe`: both callers have to read the
   * previous value anyway, to decide which §G6 toast to raise, so a flip-only action would
   * be a second way to reach the same state that no one can actually use.
   */
  setAoiClip: (on: boolean) => void;
  /** §G6 swipe; the mirror of `setAoiClip` — switching it on releases the AOI clip.
   * Switching it off also drops any `swipePair`: the handle is gone, so the second raster
   * has nothing left to be the other side of. */
  setSwipe: (on: boolean) => void;
  /** UC2's land-cover chip — see `AppState.swipePair`. `null` ends the comparison. */
  setSwipePair: (pair: { left: string; right: string } | null) => void;
  /** §F5.10/§H2: which AOI panel the analysis dock has focused (M4). */
  setAoiFocus: (id: string | null) => void;

  /* --------------------------------------- M4 §F5.6(e)/§F6 analysis result layers */

  /**
   * Register one entry of a finished run's `result_layers` (§F6 "optional result layers on
   * map (registered in legend block (e))").
   *
   * **Idempotent on `spec.id`**, which every tool builds as `{tool}:{job_id}`: a reader who
   * re-opens the same result — or a §E7 cache hit that answers the same job twice — gets
   * the layer refreshed in place rather than a second copy of it stacked on the first.
   * Replacing in place also keeps the list in the order the runs happened, which is the
   * order the legend prints.
   */
  addResultLayer: (spec: ResultLayerSpec, meta: ResultLayerMeta) => void;
  /** §F5.6(e)'s per-row ✕ — one result off the map. */
  removeResultLayer: (id: string) => void;
  /** §F5.6(e)'s block ✕ and §F6's "Clear result layers (n)" — the same action, one list. */
  clearResultLayers: () => void;
  /**
   * Ask the map to frame these bounds — §H5's "Show change on map", which both registers
   * the overlay and takes the reader to it. See `fitRequest` for why it is a request rather
   * than a call.
   */
  fitBounds: (bounds: [[number, number], [number, number]]) => void;

  /* -------------------------------------------------------- §F7 dashboard (local only) */

  /** §F7 dashboard Filters card: the local domain pick. See `dashDomain`. */
  setDashDomain: (domain: string | null) => void;
  /** §F7 dashboard Filters card: the local year pick. See `dashYear`. */
  setDashYear: (year: number | null) => void;

  /* ------------------------------------------------------ Task 6 §6 intelligence pane */

  /**
   * The `#intelSelect` pick. Resets every other field of `intel` (a new use case starts on
   * no unit, no candidates, no comparison, step 0) and clears every `intel:*` result layer
   * — the previous use case's choropleth/highlight must not survive onto the next one's map.
   */
  setIntelUseCase: (useCase: string | null) => void;
  /** Map click (`selectedUnit`) or the pane's own `#intelUnitPick` — the unit under study. */
  setIntelUnit: (unit: string | null) => void;
  /** UC2 "Find comparable landscapes" — the `k` candidate unit ids. */
  setIntelCandidates: (candidates: string[]) => void;
  /** UC2 "Compare" — which candidate is shown beside the reference unit. */
  setIntelCompareWith: (compareWith: string | null) => void;
  /** UC3's stepper position (`#uc3Step-<n>`). */
  setIntelStep: (step: number) => void;
  /**
   * Fix round 1: record that an intel control just switched `id` on or off — an
   * `#intelUnderlying` toggle. Purely
   * bookkeeping: the caller still calls `setActiveRaster`/`toggleLayer` itself; this only
   * keeps `intel.layersToggled` in step with what those calls just did, so `resetIntel` and
   * `setIntelUseCase` know what is theirs to turn back off.
   */
  setIntelLayerToggled: (id: string, on: boolean) => void;
  /**
   * `#intelReset` — back to `INITIAL_INTEL_STATE`, **the use case included** (client round
   * 2 Oct 2026: Reset is "back to the map I started on", not "start this use case over";
   * the dropdown falls back to its placeholder).
   *
   * Everything the use case owned goes with it: every `intel:*` result layer, every layer
   * `intel.layersToggled` names (an `#intelUnderlying` toggle, a chip's raster), the swipe
   * pair, the reporting level it asserted, and the clicked unit. The map is then handed
   * back the programme extent (`fitRequest`) and the boot default view (`defaultViewSeq`).
   */
  resetIntel: () => void;
  /** Amendment 1 §1: `#intelTab-*` — which of the three tabs is showing. */
  setIntelTab: (tab: IntelTab) => void;
  /** Design doc `2026-09-28-intel-subtabs-design.md` §1: `#uc1Tab-*`/`#uc2Tab-*` — which
   * second-level tab is showing inside the UC1/UC2 result panes. */
  setIntelSubTab: (subTab: string) => void;
  /** `#uc1Chip-*` — which constraint (or `'priority'`) the UC1 map is drawn from. */
  setIntelUc1Chip: (uc1Chip: string) => void;
  /** `#uc2Chip-*` — which evidence chip the UC2 map is drawn from. */
  setIntelUc2Chip: (uc2Chip: string) => void;
  /** `#uc2Practice` — the practice group UC2's chip, chart and strip all read. */
  setIntelUc2Practice: (uc2Practice: string) => void;
  /**
   * Amendment 1 §A: the My Analysis tab's own tool switch (`ToolHome`'s card grid, or the
   * one tool that is open) — what a Help card's `{tool}` target opens, and what `ToolFrame`'s
   * "← All tools" clears back to `null`.
   */
  setIntelDockTool: (tool: ToolId | null) => void;
}

/** §F5.2 *Map mode* Seg — "[Single indicator | Bivariate (two)]". */
export type MapMode = 'single' | 'bivariate';

/** §F5.5 "Max 2 overlays (toast)" — fill + graduated circles + graduated rings. */
export const MAX_OVERLAYS = 2;

/**
 * §F5.2 default per-layer opacity. A first-click layer has no entry in `layerOpacity`, so
 * every consumer reads `layerOpacity[id] ?? DEFAULT_LAYER_OPACITY` rather than seeding the
 * map on toggle.
 *
 * **Client, 2026-09-15: "for all the data, keep the default opacity at 100 % until the
 * user decides otherwise."** This used to be the mockup's `fillOpacity: .58` for a
 * thematic raster; the client instruction overrides that mockup value outright, for every
 * kind this constant reaches (raster and vector alike) — a layer draws at full strength
 * until the reader drags its own slider down.
 */
export const DEFAULT_LAYER_OPACITY = 1;

export type AppStore = AppState & AppActions;

/** §D1 `/meta/config.years` — mirrored client-side so boot does not block on the API. */
export const YEARS = [2021, 2022, 2023, 2024, 2025, 2026];

/** Last of `YEARS`; the value `selectYear` falls back to if `yearIdx` is ever out of range. */
export const FALLBACK_YEAR = 2026;

/**
 * **A cold load maps no indicator** — client specification S3, 2026-09-08.
 *
 * It used to map `b21`: §F5.2's list was long and an empty map is not a landing page, so
 * the programme's headline contractual indicator was the map that best answered "what is
 * this portal". S3 moves every indicator out of the explorer's body and behind a rail icon
 * that guests cannot use, so an indicator on a cold, signed-out map is now a surface the
 * specification says must not be there.
 *
 * What a cold load opens on instead is a **dataset**: the latest land cover across the
 * four-country extent, from the client's own per-theme table (`/meta/config.theme_defaults`
 * → `features/map/DefaultView.tsx`). Nothing here names it — the table is the server's.
 *
 * `null` rather than a constant, so there is no id for a later caller to fall back to by
 * accident: `PermalinkSync` drops an indicator a stale link names rather than substituting
 * one, which is the same rule it already applied to overlays and to the bivariate Y.
 */
export const DEFAULT_INDICATOR: string | null = null;

/**
 * `years[yearIdx]` as one selector — the year every §F1 `['values', …]` query key uses.
 * `setYearIdx` clamps, so the fallback is unreachable outside a hand-written setState.
 */
export const selectYear = (s: Pick<AppState, 'years' | 'yearIdx'>): number =>
  s.years[s.yearIdx] ?? FALLBACK_YEAR;

export const COUNTRY_NAMES: Record<Country, string> = {
  ALL: 'All countries',
  BFA: 'Burkina Faso',
  MLI: 'Mali',
  NER: 'Niger',
  NGA: 'Nigeria',
};

/**
 * The client's persona display names — S3's cross-cutting clause, **labels only**.
 *
 * The authority is the server (`/meta/config.personas`, `apps/accounts/personas.py`); this
 * is the fallback a render uses before that payload lands and the default `t()` argument
 * for the two places that print a role (§F3's topbar control and §F5.10's status bar). No
 * machinery moved with the words: `Role` is unchanged, the §I1 matrix is unchanged, and
 * every gate still compares role codes.
 *
 * `field` and `partner` deliberately carry the same label — the client's own gloss puts
 * "Field Team" and "Partner organisations" inside one persona. `/meta/config.personas`
 * carries `members` for the one control that has to tell them apart (the demo switcher).
 */
export const ROLE_NAMES: Record<Role, string> = {
  public: 'Guest',
  field: 'Soil Values Team',
  partner: 'Soil Values Team',
  analyst: 'Soil Values Team (MEL)',
  manager: 'Administrator',
};

/**
 * §F4: the splash opens on the **first load of a browser session** and not again, unless
 * the reader asks for it with the "Overview" nav button.
 *
 * `sessionStorage`, not `localStorage`: spec §F4 calls the suppression optional and scopes
 * it to the session, and that is the right scope — a reader who comes back tomorrow is
 * arriving fresh and the landing page is the thing that tells them what this portal is. A
 * `localStorage` flag would show it once, ever, to each browser.
 *
 * Reading it can throw: Safari's private mode and a locked-down enterprise profile both
 * make `sessionStorage` a property access that raises rather than a null. A splash is not
 * worth a white screen, so a failure means "show it", which is the same answer as a first
 * visit.
 */
export const SPLASH_SEEN_KEY = 'sv.splashSeen';

/**
 * The client review's R1 sign-in gate, suppressed by the same rule and for the same reason.
 *
 * A **second** key, not a reuse of `SPLASH_SEEN_KEY`: the two surfaces are dismissed
 * separately (the gate always first) and are reopened by different things — the topbar's
 * "Overview" button raises the splash again, and nothing raises the gate again inside a
 * session. One shared flag would mean closing the gate also marked the splash seen, i.e.
 * the landing page nobody had been shown yet would never appear.
 */
export const GATE_SEEN_KEY = 'sv.gateSeen';

/**
 * Reading `sessionStorage` can throw: Safari's private mode and a locked-down enterprise
 * profile both make it a property access that raises rather than a null. Neither a splash
 * nor a gate is worth a white screen, so a failure means "show it", which is the same
 * answer as a first visit; a failed *write* just means it opens again next load.
 */
function sessionFlag(key: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markSessionFlag(key: string): void {
  try {
    globalThis.sessionStorage?.setItem(key, '1');
  } catch {
    /* see `sessionFlag` */
  }
}

export function splashSuppressed(): boolean {
  return sessionFlag(SPLASH_SEEN_KEY);
}

export function suppressSplash(): void {
  markSessionFlag(SPLASH_SEEN_KEY);
}

export function gateSuppressed(): boolean {
  return sessionFlag(GATE_SEEN_KEY);
}

export function suppressGate(): void {
  markSessionFlag(GATE_SEEN_KEY);
}

/**
 * Responsive shell (client, 2026-09-14). Below 900px the pane and rail are a drawer over the
 * map (`base.css`, "Responsive shell") rather than a column beside it, so a cold load on a
 * phone or a portrait tablet shows the map and `#collapseSb` opens the drawer on request.
 * Read once at boot (`lib/viewport.ts`, which the legend and hierarchy widgets share).
 */
function defaultSidebarOpen(): boolean {
  return !isCompactViewport();
}

/**
 * Defaults per §F1 + the mockup's initial `state`.
 *
 * `splashOpen` is **false here and true in the store**: this object is the pure,
 * environment-free default that `shareHash`/`applyHash` round-trip against and that
 * `state.test.ts` asserts field by field, and making it read `sessionStorage` would make a
 * permalink test depend on which test ran before it. The session-derived value belongs to
 * the *store*, which is the thing with a lifetime — see `useAppStore`.
 */
export const initialState: AppState = {
  role: 'public',
  user: null,
  country: 'ALL',
  scope: null,
  reportingLevel: 1,
  lastAdminLevel: 1,
  // Nobody has stated a level: a cold map's regions are the cascade's answer, not a choice,
  // so the P1(b) snap is free to move it and free to move it back. See `levelPinned`.
  levelPinned: false,
  // S3: nothing is mapped on a cold load. See `DEFAULT_INDICATOR`.
  indicator: DEFAULT_INDICATOR,
  overlays: [],
  biv: { on: false, y: null },
  years: YEARS,
  yearIdx: YEARS.length - 1,
  // Nothing has chosen this year: it is the last of the programme window, and the
  // catalogue's `default_year` replaces it the moment `/indicators` answers.
  yearPinned: false,
  yearChosen: false,
  // Annual by default: the year on the slider means that year. An accumulating default
  // would make the map answer a question the reader did not ask (see `AppState`).
  cumulative: false,
  layersOn: new Set<string>(),
  layerOpacity: {},
  epochOn: {},
  ndviMask: null,
  indStyle: { ramp: null, method: 'quantile', k: 5, opacity: 0.78 },
  legendMode: 'values',
  domFilter: '',
  layerQuery: '',
  // Which basemap a cold boot starts on — **before `/meta/config` answers**. The real
  // default is the deployment's (`basemaps.default`, adopted by `app/BasemapDefault.tsx`);
  // this is the value the first render uses and the value a keyless deployment keeps.
  //
  // **OSM standard, not Carto light.** The programme's readers work at commune and watershed
  // scale, where the question is usually "which village, which road, which river is this" —
  // and CARTO's keyless light_all is both a deliberately faint canvas and, since August 2026,
  // a watermarked one. OSM carries the place names, roads and waterways at the zooms this map
  // is actually used at. A deployment holding real CARTO tiles serves `light` as its default
  // and the app adopts it; one without keeps this.
  //
  // Nothing about sharing changes: `serialize` always emits `bm=` (permalink.ts), so every
  // link ever written names its basemap explicitly and still restores exactly that one —
  // pinned, so the served default cannot overrule it. Only a URL with no `bm=` at all — a
  // bare `/map`, a hand-written hash — lands here.
  basemap: 'streets',
  basemapPinned: false,
  aoi: null,
  aoiClip: false,
  aoiFocus: null,
  results: [],
  fitRequest: null,
  defaultViewSeq: 0,
  swipe: false,
  swipePair: null,
  selectedUnit: null,
  lastClickLatLng: null,
  dockInd: null,
  analysisOpen: false,
  animateOpen: false,
  splashOpen: false,
  gateOpen: false,
  recordId: null,
  tourStep: -1,
  sidebarOpen: defaultSidebarOpen(),
  sidebarAutoHidden: false,
  dashDomain: null,
  dashYear: null,
  intel: { ...INITIAL_INTEL_STATE },
};

/**
 * Where `'watershed'` goes when §M3.5 takes the radio away — the mapped indicator turned
 * out to have no native watershed values.
 *
 * Unscoped that is `lastAdminLevel`, as it always was. **Under a watershed scope it cannot
 * be**: the subtree holds no admin units at all, so demoting to level 1 or 2 would ask §D3
 * for regions inside a watershed and paint nothing. Partnerships are the honest answer
 * there — §E4 area-weights them from the same admin cells the watershed estimate came from,
 * and they are inside the filter the reader set.
 */
function demotedLevel(s: Pick<AppState, 'scope' | 'lastAdminLevel'>): ReportingLevel {
  return levelWithinScope(s.scope, s.lastAdminLevel, PARTNERSHIP_LEVEL);
}

/**
 * The level the P1(b) snap should measure from — the **un-snap**.
 *
 * `current` is the level in the store, which after one snap is the previous indicator's
 * answer rather than anything the reader or the cascade said. Snapping from it makes the
 * snap one-way: `snapLevelToData` keeps `wanted` whenever the new indicator also has data
 * there, so a roll-up indicator (which has data everywhere) inherited whatever stratum the
 * baseline had borrowed and stayed on it, with nothing on screen able to ask for R5's
 * regions back.
 *
 * So an unpinned level is re-derived from the cascade before the snap runs, and the snap is
 * therefore idempotent in both directions: it moves the level off the cascade's answer while
 * the mapped indicator needs it to, and back onto it the moment one does not.
 *
 * Three guards, all of which mean "do not act":
 *
 * * **pinned** — the reader picked it in the chooser, or a link stated it. `levelPinned`.
 * * **`available` is empty** — the catalogue has not answered, and `snapLevelToData`'s first
 *   rule is that "we do not know yet" is never read as "there is nothing here". Re-deriving
 *   there would move the level on a catalogue that has said nothing, which is the one thing
 *   every caller of this pair is written not to do.
 * * **§M3.5 has already moved it** — `current` differs from the stored level only when the
 *   watershed demotion fired, and that demotion is an answer about *this* indicator arrived at
 *   from `lastAdminLevel` and the scope. Re-deriving over it would throw that away and hand
 *   the snap back the very `'watershed'` the demotion has just rejected. In practice the two
 *   agree (a demotion only fires where `available` has no `'watershed'`, so the snap moves off
 *   it either way) — but agreeing by construction is better than agreeing by arithmetic.
 */
function snapFrom(
  s: Pick<AppState, 'scope' | 'levelPinned' | 'reportingLevel'>,
  current: ReportingLevel,
  available: readonly ReportingLevel[],
): ReportingLevel {
  if (s.levelPinned || !available.length) return current;
  return current === s.reportingLevel ? cascadeLevel(s.scope) : current;
}

/**
 * E4: a write that can switch the scope mask on takes swipe off with it.
 *
 * The mirror of `setAoiClip`'s `swipe: false`, and needed in four actions rather than one
 * because the mask has no switch of its own: it comes on when a filtered geography and a
 * raster meet, whichever of the two arrived last, and the geography can arrive as a scope
 * (`setScope`) or as the cascade's country rung (`setCountry`). Written as a wrapper so the
 * rule is one expression read against the state the patch is about to produce, rather than
 * four copies of a condition that would drift.
 */
function releasingSwipe(s: AppStore, patch: Partial<AppState>): Partial<AppState> {
  return s.swipe && scopeMaskActive({ ...s, ...patch }) ? { ...patch, swipe: false } : patch;
}

/**
 * Fix round 1: turn off every id `intel.layersToggled` still names — `resetIntel` and
 * `setIntelUseCase`'s shared cleanup, so the two can never disagree about what "back to
 * nothing intel turned on" means. A raster id is removed the way `setActiveRaster(null)`
 * would (also dropping its `epochOn` entry and any `ndviMask` stencilled over it); a vector
 * id is simply dropped from `layersOn` (`toggleLayer(id, false)`'s whole effect). Ids the
 * ordinary Layers pane switched on are untouched — only ids this list names are intel's own.
 */
function releasingIntelLayers(
  s: Pick<AppState, 'layersOn' | 'epochOn' | 'ndviMask'>,
  toggled: readonly string[],
): Partial<AppState> {
  if (!toggled.length) return {};
  const layersOn = new Set(s.layersOn);
  let removedRaster = false;
  for (const id of toggled) {
    if (!layersOn.delete(id)) continue;
    if (isRasterLayerId(id)) removedRaster = true;
  }
  if (!removedRaster) return { layersOn };
  const epochOn: Record<string, string> = {};
  for (const [rid, date] of Object.entries(s.epochOn)) if (layersOn.has(rid)) epochOn[rid] = date;
  return { layersOn, epochOn, ndviMask: null };
}

/**
 * "Country selector is global" (§F1): every derived view reads this one field, so
 * changing it also drops the unit selection made under the previous geography.
 */
export const useAppStore = create<AppStore>((set) => ({
  ...initialState,
  // §F4: open on the session's first load. Evaluated once, when the store is created —
  // which is once per page load, i.e. exactly the granularity "first load" means.
  splashOpen: !splashSuppressed(),
  // R1, the same rule one step earlier: the gate is what a cold session actually lands on,
  // and the splash waits behind it (`features/landing/index.tsx`).
  gateOpen: !gateSuppressed(),
  // E4 (round 5): the country rung raises the mask on its own, so this is the fourth write
  // that has to release a running swipe — see `releasingSwipe`. It reads the patch, so the
  // case where the new country *drops* an out-of-country scope is counted correctly: the mask
  // moves from the region's outline to the country's, and stays up.
  setCountry: (country) =>
    set((s) =>
      releasingSwipe(s, {
        country,
        selectedUnit: null,
        // §F5.8: the two geography controls are one geography. Moving the global selector to a
        // country the scope is not in would leave the map filtered to a region of somewhere
        // else — so the narrower filter yields to the broader one. `ALL` contains every scope
        // and clears nothing; a cross-border watershed (`country: null`) cannot be *proved*
        // inside the new country and is dropped, which is the explainable answer.
        // R5: out of a filter is back to the unfiltered level, which is regions.
        // `levelPinned: false` with it: the level being written here is the cascade's, so a
        // level the reader had stated for the geography they have just left must not go on
        // holding the snap off in the new one.
        ...(s.scope && country !== 'ALL' && s.scope.country !== country
          ? {
              scope: null,
              reportingLevel: NO_SCOPE_LEVEL,
              lastAdminLevel: 1 as const,
              levelPinned: false,
            }
          : {}),
      }),
    ),
  setScope: (scope) =>
    set((s) =>
      // E4: filtering with a raster on is what raises the scope mask, so it is one of the
      // three writes that release swipe (`releasingSwipe`).
      releasingSwipe(s, {
        scope,
        ...(scope.country && scope.country !== s.country && scope.country in COUNTRY_NAMES
          ? { country: scope.country as Country }
          : {}),
        reportingLevel: levelWithinScope(scope, s.reportingLevel, defaultScopeLevel(scope)),
        // The previously selected unit is almost certainly outside the new filter, and a
        // profile panel describing a unit the map no longer draws is the §F5.11 case the
        // country selector already handles this way.
        selectedUnit: null,
      }),
    ),
  // Back to regions, not to the level the scope forced: a region scope maps communes, and
  // clearing the filter must not leave the reader on a programme-wide 1 048-polygon commune
  // map they never asked for.
  //
  // Regions rather than `lastAdminLevel` since client review R5 (`NO_SCOPE_LEVEL`): with the
  // radios gone there is no level a reader can have been on *other* than the one the cascade
  // implied, so "the level before the filter" and "the unfiltered level" are one fact. This
  // also fixes the shared-link case the old rule got wrong from the other side — a restored
  // `sc=<region>&lv=2` link had `lastAdminLevel: 2` hydrated straight from the hash, and
  // clearing its filter opened the programme-wide commune map.
  //
  // **D3 adds the country.** The cascade's country box is the first of its five rungs, so
  // "clear the filter" that left `BFA` standing cleared four rungs out of five and the map
  // stayed inside one country — the state IFDC described as "not back to the first level".
  // One condition covers both halves: there is something to clear if either field is set.
  clearScope: () =>
    set((s) =>
      s.scope || s.country !== 'ALL'
        ? {
            scope: null,
            country: 'ALL' as const,
            reportingLevel: NO_SCOPE_LEVEL,
            lastAdminLevel: 1 as const,
            // "Starting again" includes the level: the reader is back on the cascade's own
            // answer, so the P1(b) snap may move it again (see `levelPinned`).
            levelPinned: false,
          }
        : {},
    ),
  // §F5.7 "Updates live on year/**level** changes" and §F5.8 "Radio change → re-render
  // choropleth/overlays/**unit panel**": a reporting-level change re-aggregates the map,
  // but the selected unit is still the same place and its profile must follow, not vanish.
  // (Only the *country* filter closes the panel — §F5.11.) NOTES #202.
  // `lastAdminLevel` rides along so the §M3.5 fallback has somewhere to go back to; it is
  // written here rather than derived because once `reportingLevel` is `'watershed'` the
  // admin level the reader came from is no longer anywhere in the state.
  // `levelPinned` because this action has exactly two callers and both of them are somebody
  // stating a level: the §F5.8 `<LevelChooser>`, and `PermalinkSync` re-asserting a shared
  // link's `lv=` on the path where the link's filter turned out not to exist. Neither is the
  // cascade, so neither may be quietly undone by the next snap (see `levelPinned`).
  setReportingLevel: (reportingLevel) =>
    set(
      isOverlayLevel(reportingLevel)
        ? { reportingLevel, levelPinned: true }
        : { reportingLevel, lastAdminLevel: reportingLevel, levelPinned: true },
    ),
  reconcileReportingLevel: (indicatorId, watershedSupported, available = []) =>
    set((s) => {
      // Read inside `set`, so the comparison is against the store as it is *now* and not as it
      // was when the caller rendered — that is the whole point (see the interface).
      if (s.indicator !== indicatorId) return {};
      // §M3.5 first, P1(b) second, over the level §M3.5 leaves: a `'watershed'` level the
      // indicator has no native values for is not a level to then snap *from*.
      const demoted =
        s.reportingLevel === WATERSHED_LEVEL && !watershedSupported
          ? demotedLevel(s)
          : s.reportingLevel;
      const snapped = snapLevelToData(s.scope, snapFrom(s, demoted, available), available);
      return snapped === s.reportingLevel ? {} : { reportingLevel: snapped };
    }),
  // §M3.5's year twin: `b21` now carries 2025–26 and nothing else, so a reader arriving on
  // the default 2026 is fine but one who drags to 2021 and *then* picks b21 would be left
  // on an empty map. Snapping to the newest year the indicator actually has is the same
  // move `reconcileReportingLevel` makes for the level — and it is deliberately a snap to
  // the *newest*, not the nearest: a programme reader wants the current figure, and a
  // cumulative series' newest year is the one the headline number comes from.
  // The cold-load year. `/indicators` is the only thing that knows how much of a year has
  // actually been reported, so the boot year cannot be a constant: the last programme year
  // is 2026 and B.2.1's 2026 is three rows, which opened the portal on a single painted
  // polygon (NOTES #361's "one thing to say out loud to the client"). Applied once, and
  // never over a year the reader or a permalink has already chosen.
  applyDefaultYear: (year) =>
    set((s) => {
      if (s.yearPinned || year === null || year === undefined) return {};
      const idx = s.years.indexOf(year);
      // Pinned either way: a `default_year` outside this deploy's programme window is still
      // an answer, and re-asking on every catalogue refetch would only churn.
      return idx >= 0 ? { yearIdx: idx, yearPinned: true } : { yearPinned: true };
    }),
  snapYearToData: (available, preferred = null) =>
    set((s) => {
      const current = s.years[s.yearIdx];
      if (!available.length || (current !== undefined && available.includes(current))) {
        return {};
      }
      // The indicator's own default first — the same year a cold load would have opened on
      // — and the newest year with data only when that is not one of them. Snapping to the
      // newest was the old rule and it lands on exactly the near-empty year `default_year`
      // exists to avoid.
      const wanted = preferred !== null && available.includes(preferred) ? preferred : null;
      const idx =
        wanted !== null
          ? s.years.indexOf(wanted)
          : s.years.reduce((best, year, i) => (available.includes(year) ? i : best), -1);
      return idx >= 0 ? { yearIdx: idx } : {};
    }),
  // A pick is a statement: from here on the served default may not move the map (§F5.1).
  setBasemap: (basemap) => set({ basemap, basemapPinned: true }),
  adoptDefaultBasemap: (basemap) =>
    set((s) => (s.basemapPinned || s.basemap === basemap ? {} : { basemap })),
  setRole: (role) => set({ role }),
  setSession: (user) =>
    set({
      role: user.role,
      user:
        user.id === null
          ? null
          : {
              id: user.id,
              name: user.name ?? String(user.id),
              org: user.org,
              country_scope: user.country_scope,
            },
    }),

  setIndicator: (id, hasWatershedValues = false, available = []) =>
    set((s) => {
      // §M3.5: the watershed radio is per-indicator, so the *level* is too. Clearing the
      // indicator, or picking one MELS does not report per watershed, drops the map back to
      // the admin level it came from — otherwise the widget would render with no radio
      // checked and the choropleth would ask for a stratum the indicator has no values in.
      const demoted =
        s.reportingLevel === WATERSHED_LEVEL && !(id && hasWatershedValues)
          ? demotedLevel(s)
          : s.reportingLevel;
      // P1(b): and then onto a level the new indicator actually has data at. The widget's
      // effect would reach the same answer a tick later, but a tick is a request: the map
      // would ask §D3 for the region values of a survey estimate, paint the empty result,
      // and repaint. Doing it in the same write means the first fetch is the right one.
      // …and `snapFrom` is what makes that move reversible: an unpinned level is re-derived
      // from the cascade first, so mapping a baseline series and then going back to a roll-up
      // one puts R5's regions back rather than leaving the reader on the stratum the baseline
      // borrowed. See `snapFrom`.
      const level = id
        ? snapLevelToData(s.scope, snapFrom(s, demoted, available), available)
        : demoted;
      return {
        indicator: id,
        // §F5.2 "reset indStyle.ramp" — a fresh auto-symbology (§G2) per indicator.
        indStyle: { ...s.indStyle, ramp: null },
        // A no-longer-mapped indicator must not leave a stale profile behind (§F5.7).
        ...(id === null ? { selectedUnit: null } : {}),
        ...(level === s.reportingLevel ? {} : { reportingLevel: level }),
      };
    }),
  setIndStyle: (patch) => set((s) => ({ indStyle: { ...s.indStyle, ...patch } })),
  setYearIdx: (idx) =>
    set((s) => ({
      yearIdx: Math.min(Math.max(0, Math.trunc(idx)), s.years.length - 1),
      // The reader has chosen a year; a catalogue landing a moment later must not take it.
      yearPinned: true,
      yearChosen: true,
    })),
  setCumulative: (cumulative) => set({ cumulative }),
  setLegendMode: (legendMode) => set({ legendMode }),
  setDomFilter: (domFilter) => set({ domFilter }),
  setSelectedUnit: (selectedUnit) => set({ selectedUnit }),
  setLastClickLatLng: (lastClickLatLng) => set({ lastClickLatLng }),
  // Opening the pane by hand is the reader taking it back: whatever the dock collapsed is
  // no longer the dock's to restore (see `sidebarAutoHidden`). Closing it by hand leaves
  // the flag alone — a dock that is already holding the pane closed has not stopped doing
  // so because the reader pressed the same button.
  setSidebarOpen: (sidebarOpen) =>
    set(sidebarOpen ? { sidebarOpen, sidebarAutoHidden: false } : { sidebarOpen }),
  // E2. `indStyle.ramp` is reset with the indicator for the reason §F5.2 gives on
  // `setIndicator`: a ramp chosen for one indicator must not survive onto the next one the
  // reader maps. The empty-return branches keep this a no-op when the other side is already
  // empty, so a reader clicking between tabs on a bare map re-renders nothing.
  switchSidebarTab: (tab) =>
    set((s) => {
      if (tab === 'layers') {
        if (!selectIndicatorMapped(s)) return {};
        return {
          indicator: null,
          indStyle: { ...s.indStyle, ramp: null },
          overlays: [],
          biv: { on: false, y: null },
          // §F5.7: a profile panel outliving the indicator it described is the same stale
          // panel `setIndicator(null)` closes.
          selectedUnit: null,
        };
      }
      return selectLayersMapped(s) ? { layersOn: new Set<string>() } : {};
    }),
  setAnalysisOpen: (analysisOpen) =>
    // §F6 opens over the map, and the mockup's own handler hides the info panel when it
    // does. The unit profile is this portal's `#infopanel`: 390 px of dock next to 360 px
    // of profile leaves no map between them.
    //
    // Task 6 §6.3: closing the dock also resets the `#intelSelect` pick back to "no use
    // case chosen" — the dropdown has no memory across a close/reopen, exactly like `tool`
    // used to (the dock's own `useState`, before Amendment 1 lifted it to `intel.dockTool`,
    // which resets here the same way). Its result layers are left alone: they are map
    // content like any other tool's, cleared by `#intelReset` or the reader's own ✕, not by
    // closing the panel that produced them.
    //
    // Amendment 1 §1: `tab` is the one field that survives — "a reopened dock returns to
    // the same tab" — so a reader who closed the dock mid-Help is not dropped back on
    // Use Cases the next time they open it.
    //
    // Client round, 3 October 2026: the dock also **borrows the sidebar**. 332 px of pane
    // and 390 px of dock leave no map between them, so opening collapses the pane and
    // remembers that it did (`sidebarAutoHidden`); closing hands it back, but only if the
    // dock is the one that took it. A reader who had already collapsed the sidebar keeps
    // it collapsed, and one who re-opened it while the dock was up keeps it open — the
    // flag is cleared by their own `setSidebarOpen(true)`.
    //
    // The map re-layouts by itself: `#mapwrap` is a flex child of the sidebar's row, so the
    // collapse changes the map container's width and `MapView`'s `ResizeObserver` calls
    // `invalidateSize` — the same path the `#collapseSb` button has always used, and the
    // reason neither of them reaches for the map here.
    set((s) =>
      analysisOpen
        ? {
            analysisOpen,
            selectedUnit: null,
            ...(s.sidebarOpen ? { sidebarOpen: false, sidebarAutoHidden: true } : {}),
          }
        : {
            analysisOpen,
            intel: { ...INITIAL_INTEL_STATE, tab: s.intel.tab },
            ...(s.sidebarAutoHidden ? { sidebarOpen: true, sidebarAutoHidden: false } : {}),
          },
    ),
  setAnimateOpen: (animateOpen) => set({ animateOpen }),
  setSplashOpen: (splashOpen) => {
    if (!splashOpen) suppressSplash();
    set({ splashOpen });
  },
  setGateOpen: (gateOpen) => {
    if (!gateOpen) suppressGate();
    set({ gateOpen });
  },
  openRecord: (recordId) => set({ recordId }),
  closeRecord: () => set({ recordId: null }),

  toggleLayer: (id, on) =>
    set((s) => {
      const next = new Set(s.layersOn);
      const wanted = on ?? !next.has(id);
      // R4: a second raster is refused, not swapped in. Swapping would make the checkbox a
      // radio without looking like one — the reader would click "add" and watch a different
      // layer disappear. The row is disabled instead, and this is its backstop.
      if (wanted && isRasterLayerId(id)) {
        const active = activeRasterOf(s.layersOn);
        if (active !== null && active !== id) return {};
      }
      if (wanted) next.add(id);
      else next.delete(id);
      return releasingSwipe(s, { layersOn: next });
    }),
  // D2's raster select, as one atomic write. `RASTER_IDS` is the same register
  // `toggleLayer` and `activeRasterOf` read, so a catalogue that has not landed yet means
  // "nothing is a raster": the pick is simply added, and `registerRasterLayers` normalises
  // the set the moment the rule becomes evaluable — the permalink path, unchanged.
  // Task 6: the same write also drops `epochOn` for whichever raster this replaces (a stale
  // date must not resurface if the reader switches back — the newest epoch is always the
  // honest default for a series nobody has an active pick for any more) and clears `ndviMask`
  // the moment the newly-drawn raster is not one of `NDVI_SERIES` — a mask stencilled over
  // `soc` would be a crop mask over a soil map, which is not what the reader asked to see.
  setActiveRaster: (id, rasterIds) =>
    set((s) => {
      const rasters = rasterIds ? new Set(rasterIds) : RASTER_IDS;
      const next = new Set<string>();
      for (const on of s.layersOn) if (!rasters.has(on)) next.add(on);
      if (id) next.add(id);
      const epochOn: Record<string, string> = {};
      for (const [rid, date] of Object.entries(s.epochOn)) if (next.has(rid)) epochOn[rid] = date;
      const ndviMask = id && (NDVI_SERIES as readonly string[]).includes(id) ? s.ndviMask : null;
      return releasingSwipe(s, { layersOn: next, epochOn, ndviMask });
    }),
  // Clamped: the §F5.2 slider is 10–100 %, and a layer at 0 is indistinguishable from a
  // layer that failed to load — the reader would blame the tiles, not the control.
  setLayerOpacity: (id, opacity) =>
    set((s) => ({
      layerOpacity: { ...s.layerOpacity, [id]: Math.min(1, Math.max(0.1, opacity)) },
    })),
  setLayerQuery: (layerQuery) => set({ layerQuery }),

  setEpoch: (id, date) =>
    set((s) => {
      if (date === null) {
        if (!(id in s.epochOn)) return {};
        const epochOn = { ...s.epochOn };
        delete epochOn[id];
        return { epochOn };
      }
      return { epochOn: { ...s.epochOn, [id]: date } };
    }),
  setNdviMask: (ndviMask) => set({ ndviMask }),

  addOverlay: (id) =>
    set((s) =>
      s.overlays.some((o) => o.id === id) || s.overlays.length >= MAX_OVERLAYS
        ? {}
        : { overlays: [...s.overlays, { id }] },
    ),
  removeOverlay: (id) => set((s) => ({ overlays: s.overlays.filter((o) => o.id !== id) })),
  // A cleared override drops the `color` key entirely rather than storing `undefined`:
  // §G4 reads `if (overlays[i].color)`, and the permalink (Task 8) serialises the array.
  setOverlayColor: (id, color) =>
    set((s) => ({
      overlays: s.overlays.map((o) => (o.id === id ? (color ? { id: o.id, color } : { id: o.id }) : o)),
    })),
  clearOverlays: () => set((s) => (s.overlays.length ? { overlays: [] } : {})),

  setMapMode: (mode) =>
    set((s) => {
      if (mode === 'bivariate') {
        // Re-selecting the mode already in force must not throw away a chosen Y — the
        // §F5.2 Seg fires on every click, not only on a change.
        if (s.biv.on) return {};
        return { biv: { on: true, y: null }, overlays: [] };
      }
      return s.biv.on ? { biv: { on: false, y: null } } : {};
    }),
  setBivY: (id) => set((s) => (s.biv.on ? { biv: { on: true, y: id } } : {})),

  toggleDockInd: (id) => set((s) => ({ dockInd: s.dockInd === id ? null : id })),
  setDockInd: (dockInd) => set({ dockInd }),

  // The id is derived here and nowhere else, so `aoi.id` is by construction the identity of
  // `aoi.pts` (M4, `lib/aoi.ts`). The permalink is the one other writer of this field and it
  // calls `ringId` itself, because it writes with `setState` rather than the actions.
  setAoi: (aoi) =>
    set(
      aoi
        ? { aoi: { pts: aoi.pts, areaKm2: aoi.areaKm2, id: ringId(aoi.pts) } }
        : { aoi: null, aoiClip: false, aoiFocus: null },
    ),
  setAoiClip: (on) =>
    set((s) => {
      if (!on) return s.aoiClip ? { aoiClip: false } : {};
      if (!s.aoi) return {}; // nothing to clip to — §F5.10 draws the AOI first
      // `swipePair` goes with the handle, exactly as it does in `setSwipe(false)`: leaving
      // the pair behind would keep UC2's second COG on `swipeLeftPane` with no handle to
      // split it from the first, which reads as two rasters stacked.
      return { aoiClip: true, swipe: false, swipePair: null };
    }),
  // E4: refused while the scope mask would hold the raster pane — the §G6 exclusion, extended
  // to the third writer of `clip-path` on those panes. Asked of the state this write would
  // produce, not of the one it starts from (`swipeBlockedByScope`), because the write releases
  // the AOI clip and the AOI clip is what keeps the mask down. Silent here and explained by
  // the caller, like the AOI pair above; `MapTools` asks the same question and toasts.
  setSwipe: (on) =>
    set((s) => {
      if (!on) return s.swipe || s.swipePair ? { swipe: false, swipePair: null } : {};
      if (swipeBlockedByScope(s)) return {};
      return { swipe: true, aoiClip: false };
    }),
  // One write, so a chip can never leave the handle up with one raster under it or two
  // rasters on with no handle: a pair turns swipe on (and releases the AOI clip, like any
  // other swipe), and `null` turns both off.
  // Setting a pair is a `setSwipe(true)` with a second raster attached, so it asks the same
  // E4 question of the state it would produce: refused while the scope mask would hold the
  // raster pane, because the pair writes `clip-path` on `rasterPane` and `swipeLeftPane`
  // and the mask writes the first of those too. Without the guard a chip could reach the
  // two-writers state `setSwipe` exists to forbid.
  setSwipePair: (swipePair) =>
    set((s) => {
      if (!swipePair) return s.swipePair || s.swipe ? { swipePair: null, swipe: false } : {};
      if (swipeBlockedByScope(s)) return {};
      return { swipePair, swipe: true, aoiClip: false };
    }),
  setAoiFocus: (aoiFocus) => set({ aoiFocus }),

  addResultLayer: (spec, meta) =>
    set((s) => {
      const entry: ResultLayer = {
        id: spec.id,
        jobId: meta.jobId,
        tool: meta.tool,
        label: meta.label ?? spec.label,
        spec,
        createdAt: Date.now(),
      };
      const at = s.results.findIndex((r) => r.id === entry.id);
      if (at === -1) return { results: [...s.results, entry] };
      const results = [...s.results];
      results[at] = entry;
      return { results };
    }),
  removeResultLayer: (id) =>
    set((s) =>
      s.results.some((r) => r.id === id) ? { results: s.results.filter((r) => r.id !== id) } : {},
    ),
  clearResultLayers: () => set((s) => (s.results.length ? { results: [] } : {})),
  fitBounds: (bounds) => set((s) => ({ fitRequest: { bounds, seq: (s.fitRequest?.seq ?? 0) + 1 } })),

  setDashDomain: (dashDomain) => set({ dashDomain }),
  setDashYear: (dashYear) => set({ dashYear }),

  setIntelUseCase: (useCase) =>
    set((s) => ({
      // `tab` and `dockTool` are the other two tabs' own business — a fresh `#intelSelect`
      // pick starts *that use case* over, not the reader's place in the pane or whatever
      // they had half-configured under My Analysis (Amendment 1 §A/§1).
      intel: { ...INITIAL_INTEL_STATE, useCase, tab: s.intel.tab, dockTool: s.intel.dockTool },
      // Client round 30 Sep 2026 (NOTES #728): opening a use case clears every other map
      // layer — the mapped indicator (fill, overlays, bivariate), every raster and vector
      // layer the Layers pane switched on, and every other tool's result — so the use case's
      // own choropleth is the only thing painted. Closing/reset does not restore them.
      ...(useCase
        ? {
            indicator: null,
            indStyle: { ...s.indStyle, ramp: null },
            overlays: [],
            biv: { on: false, y: null },
            layersOn: new Set<string>(),
            results: [],
            // A land-cover comparison belongs to the chip that opened it (UC2's own), so
            // it cannot survive into another use case any more than the choropleth can.
            swipe: false,
            swipePair: null,
          }
        : {
            results: s.results.some((r) => r.id.startsWith('intel:'))
              ? s.results.filter((r) => !r.id.startsWith('intel:'))
              : s.results,
            ...releasingIntelLayers(s, s.intel.layersToggled),
          }),
    })),
  setIntelUnit: (unit) => set((s) => ({ intel: { ...s.intel, unit } })),
  setIntelCandidates: (candidates) => set((s) => ({ intel: { ...s.intel, candidates } })),
  setIntelCompareWith: (compareWith) => set((s) => ({ intel: { ...s.intel, compareWith } })),
  setIntelStep: (step) => set((s) => ({ intel: { ...s.intel, step } })),
  setIntelLayerToggled: (id, on) =>
    set((s) => {
      const has = s.intel.layersToggled.includes(id);
      if (on === has) return {};
      const layersToggled = on
        ? [...s.intel.layersToggled, id]
        : s.intel.layersToggled.filter((x) => x !== id);
      return { intel: { ...s.intel, layersToggled } };
    }),
  resetIntel: () =>
    set((s) => ({
      /**
       * Client round 2 Oct 2026: Reset is **"back to the map I started on"**, not "start
       * this use case over".
       *
       * The old reading kept `useCase` and cleared only what the use case had drawn, which
       * left the reader looking at an empty map with a use case still selected in the
       * dropdown and the reporting level still on watersheds — the one state nothing on
       * screen could get them out of except reloading. So the use case goes too (the
       * dropdown falls back to its placeholder), the map is handed back the boot view, and
       * the three things a use case silently owns are released: the level it moved
       * (`setReportingLevel(WATERSHED_LEVEL)` on open), the unit the reader clicked, and
       * any raster or swipe pair a chip took.
       *
       * `tab` and `dockTool` still survive: neither the Analysis dock tab the reader is on
       * nor a half-configured My Analysis tool is part of "the map".
       */
      intel: { ...INITIAL_INTEL_STATE, tab: s.intel.tab, dockTool: s.intel.dockTool },
      results: s.results.some((r) => r.id.startsWith('intel:'))
        ? s.results.filter((r) => !r.id.startsWith('intel:'))
        : s.results,
      selectedUnit: null,
      lastClickLatLng: null,
      // The app's own cold level (`initialState`), not the one the use case asserted, and
      // unpinned: a level nobody stated is the cascade's to move again.
      reportingLevel: 1,
      lastAdminLevel: 1,
      levelPinned: false,
      // `DefaultView`'s guard refuses to act on a map somebody has already chosen, and the
      // rail pick is part of what it reads — so the pick is cleared here and re-made by
      // `applyThemeDefault` on the other side, exactly as on a cold load.
      domFilter: '',
      fitRequest: { bounds: PROGRAMME_BOUNDS, seq: (s.fitRequest?.seq ?? 0) + 1 },
      defaultViewSeq: s.defaultViewSeq + 1,
      ...(s.swipe || s.swipePair ? { swipe: false, swipePair: null } : {}),
      ...releasingIntelLayers(s, s.intel.layersToggled),
    })),
  setIntelTab: (tab) => set((s) => ({ intel: { ...s.intel, tab } })),
  setIntelSubTab: (subTab) => set((s) => ({ intel: { ...s.intel, subTab } })),
  setIntelUc1Chip: (uc1Chip) => set((s) => ({ intel: { ...s.intel, uc1Chip } })),
  setIntelUc2Chip: (uc2Chip) => set((s) => ({ intel: { ...s.intel, uc2Chip } })),
  setIntelUc2Practice: (uc2Practice) => set((s) => ({ intel: { ...s.intel, uc2Practice } })),
  setIntelDockTool: (dockTool) => set((s) => ({ intel: { ...s.intel, dockTool } })),
}));

/** Test/helper hook: restore the documented defaults (fresh Set/objects each time). */
export function resetAppStore(): void {
  // The R4 register is catalogue-derived, so a test that seeded it must not leak into the
  // next one; whoever holds the `['layers']` query re-registers on the next arrival.
  registerRasterLayers([]);
  useAppStore.setState({
    ...initialState,
    scope: null,
    layersOn: new Set<string>(),
    layerOpacity: {},
    epochOn: {},
    ndviMask: null,
    overlays: [],
    results: [],
    fitRequest: null,
    biv: { ...initialState.biv },
    years: [...YEARS],
    indStyle: { ...initialState.indStyle },
    intel: { ...INITIAL_INTEL_STATE },
  });
}
